import React, { useState, useMemo } from 'react';
import { Zap, Search, X } from 'lucide-react';
import PerformanceCharts from './PerformanceCharts';
import ScoreBadge from './shared/ScoreBadge';
import AIAnalysisPanel from './shared/AIAnalysisPanel';
import { formatCurrency, formatNumber } from '../utils/formatters';
import GoogleOptimizationPanel from './GoogleOptimizationPanel';
import InactiveAccountCardBase from './shared/InactiveAccountCard';
import { makeClientScorer } from './shared/scoring';
import usePerformanceData from '../hooks/usePerformanceData';

// Module scope: a new array each render would change the hook's deps and refetch forever.
const FALLBACK_RANGES = ['last_30_days', 'last_14_days'];

// ==================== TIMEFRAME OPTIONS ====================
const TIMEFRAME_OPTIONS = [
  { id: 'last_7_days', label: 'Last 7 Days', days: 7 },
  { id: 'last_14_days', label: 'Last 14 Days', days: 14 },
  { id: 'last_30_days', label: 'Last 30 Days', days: 30 },
  { id: 'last_90_days', label: 'Last 90 Days', days: 90 },
];

// ==================== SCORING SYSTEM ====================
// Google Campaign Types and their scoring logic
const GOOGLE_CAMPAIGN_CONFIG = {
  'SEARCH': {
    name: 'Search',
    primary_metric: 'conversions',
    primary_label: 'Conversions',
    cost_metric: 'cpa',
    cost_label: 'CPA',
    kpis: {
      cpa: { good: 300, warning: 600, weight: 40 },  // Lower is better
      ctr: { good: 3, warning: 1.5, weight: 30 },    // Higher is better
      conv_rate: { good: 5, warning: 2, weight: 30 }  // Higher is better
    },
    focus: 'conversion efficiency, search intent, and quality score'
  },
  'PERFORMANCE_MAX': {
    name: 'Performance Max',
    primary_metric: 'conversions',
    primary_label: 'Conversions',
    cost_metric: 'cpa',
    cost_label: 'CPA',
    kpis: {
      cpa: { good: 400, warning: 800, weight: 35 },
      roas: { good: 3, warning: 1.5, weight: 35 },
      all_conv_rate: { good: 3, warning: 1, weight: 30 }
    },
    focus: 'cross-channel performance, ROAS, and automation efficiency'
  },
  'DISPLAY': {
    name: 'Display',
    primary_metric: 'conversions',
    primary_label: 'Conversions',
    cost_metric: 'cpa',
    cost_label: 'CPA',
    kpis: {
      cpa: { good: 500, warning: 1000, weight: 35 },
      ctr: { good: 0.5, warning: 0.2, weight: 25 },  // Display CTR is lower
      cpm: { good: 50, warning: 150, weight: 20 },
      view_through_rate: { good: 10, warning: 5, weight: 20 }
    },
    focus: 'brand awareness, reach, and view-through conversions'
  },
  'VIDEO': {
    name: 'Video',
    primary_metric: 'video_views',
    primary_label: 'Views',
    cost_metric: 'cpv',
    cost_label: 'CPV',
    kpis: {
      cpv: { good: 1, warning: 3, weight: 35 },  // Cost per view
      view_rate: { good: 30, warning: 15, weight: 35 },  // % of impressions that became views
      ctr: { good: 0.5, warning: 0.2, weight: 30 }
    },
    focus: 'view rate, engagement, and brand recall'
  },
  'SHOPPING': {
    name: 'Shopping',
    primary_metric: 'conversions',
    primary_label: 'Purchases',
    cost_metric: 'cpa',
    cost_label: 'CPA',
    kpis: {
      roas: { good: 4, warning: 2, weight: 40 },
      cpa: { good: 300, warning: 600, weight: 30 },
      ctr: { good: 1.5, warning: 0.8, weight: 30 }
    },
    focus: 'ROAS, product feed quality, and competitive pricing'
  },
  'DISCOVERY': {
    name: 'Discovery',
    primary_metric: 'conversions',
    primary_label: 'Conversions',
    cost_metric: 'cpa',
    cost_label: 'CPA',
    kpis: {
      cpa: { good: 400, warning: 800, weight: 35 },
      ctr: { good: 1, warning: 0.5, weight: 35 },
      engagement_rate: { good: 5, warning: 2, weight: 30 }
    },
    focus: 'discovery intent, visual engagement, and audience targeting'
  },
  'APP': {
    name: 'App',
    primary_metric: 'conversions',
    primary_label: 'Installs',
    cost_metric: 'cpi',
    cost_label: 'CPI',
    kpis: {
      cpi: { good: 50, warning: 150, weight: 40 },
      ctr: { good: 2, warning: 1, weight: 30 },
      install_rate: { good: 10, warning: 5, weight: 30 }
    },
    focus: 'install volume, cost per install, and in-app actions'
  }
};

const calculateCampaignScore = (campaign) => {
  let score = 50;
  const spend = campaign.spend || 0;
  const clicks = campaign.clicks || 0;
  const conversions = campaign.conversions || 0;
  const ctr = campaign.ctr || 0;
  const roas = campaign.roas || 0;
  const campaign_type = campaign.objective || 'SEARCH';
  
  if (spend === 0) return { score: 0, status: 'inactive', reasons: ['No spend'] };

  const reasons = [];
  const config = GOOGLE_CAMPAIGN_CONFIG[campaign_type] || GOOGLE_CAMPAIGN_CONFIG['SEARCH'];

  if (campaign.status === 'ACTIVE' || campaign.status === 'ENABLED') score += 10;

  // CTR scoring (type-aware thresholds)
  const ctrThreshold = config.kpis.ctr || { good: 2, warning: 1 };
  if (ctr >= ctrThreshold.good) { 
    score += 15; 
    reasons.push('Excellent CTR'); 
  } else if (ctr >= ctrThreshold.warning) { 
    score += 5; 
  } else if (ctr < ctrThreshold.warning * 0.5 && clicks > 0) { 
    score -= 10; 
    reasons.push('Low CTR'); 
  }

  // Conversion-based scoring
  let form_submissions = 0;
  let whatsapp_conversions = 0;
  
  if (campaign.conversion_actions) {
    const ca = campaign.conversion_actions;
    form_submissions = ca.form_submissions || 0;
    whatsapp_conversions = ca.website_conversions || 0;
  }
  
  const totalResults = conversions;
  
  if (totalResults > 0) {
    const cpa = spend / totalResults;
    const cpaThreshold = config.kpis.cpa || { good: 300, warning: 600 };
    
    if (cpa <= cpaThreshold.good) { 
      score += 25; 
      reasons.push(`Excellent CPA ₹${cpa.toFixed(0)}`); 
    } else if (cpa <= cpaThreshold.warning) { 
      score += 15; 
      reasons.push(`Good CPA ₹${cpa.toFixed(0)}`); 
    } else if (cpa <= cpaThreshold.warning * 1.5) { 
      score += 5; 
    } else { 
      score -= 10; 
      reasons.push(`High CPA ₹${cpa.toFixed(0)}`); 
    }
    
    // Bonus for form submissions 
    if (form_submissions > 0) {
      score += 3;
      reasons.push(`${form_submissions} form leads`);
    }
    
    // Bonus for WhatsApp/Chat conversions
    if (whatsapp_conversions > 0) {
      score += 3;
      reasons.push(`${whatsapp_conversions} chat leads`);
    }
  } else if (spend > 100) {
    score -= 15;
    reasons.push('No conversions');
  }

  // ROAS scoring for Shopping/PMax
  if (['SHOPPING', 'PERFORMANCE_MAX'].includes(campaign_type) && roas > 0) {
    const roasThreshold = config.kpis.roas || { good: 3, warning: 1.5 };
    if (roas >= roasThreshold.good) {
      score += 10;
      reasons.push(`Strong ROAS ${roas.toFixed(1)}x`);
    } else if (roas < roasThreshold.warning) {
      score -= 5;
      reasons.push(`Low ROAS ${roas.toFixed(1)}x`);
    }
  }

  // CPC scoring
  if (clicks > 0) {
    const cpc = spend / clicks;
    if (cpc <= 10) { 
      score += 10; 
      reasons.push('Low CPC'); 
    } else if (cpc > 50) { 
      score -= 10; 
      reasons.push('High CPC'); 
    }
  }

  // Video-specific scoring
  if (campaign_type === 'VIDEO') {
    const video_views = campaign.video_views || 0;
    if (video_views > 0) {
      const cpv = spend / video_views;
      if (cpv <= 1) {
        score += 10;
        reasons.push(`Excellent CPV ₹${cpv.toFixed(2)}`);
      } else if (cpv > 3) {
        score -= 5;
        reasons.push(`High CPV ₹${cpv.toFixed(2)}`);
      }
    }
  }

  score = Math.max(0, Math.min(100, score));
  const status = score >= 70 ? 'good' : score >= 40 ? 'warning' : 'critical';

  return { score, status, reasons };
};

const calculateClientScore = makeClientScorer(calculateCampaignScore);
const CampaignTypeBadge = ({ type }) => {
  const config = {
    'SEARCH': { name: 'Search', color: 'bg-blue-500/20 text-blue-400', icon: '🔍' },
    'DISPLAY': { name: 'Display', color: 'bg-purple-500/20 text-purple-400', icon: '🖼️' },
    'VIDEO': { name: 'Video', color: 'bg-red-500/20 text-red-400', icon: '🎬' },
    'SHOPPING': { name: 'Shopping', color: 'bg-green-500/20 text-green-400', icon: '🛒' },
    'PERFORMANCE_MAX': { name: 'PMax', color: 'bg-teal-500/20 text-teal-400', icon: '⚡' },
    'DISCOVERY': { name: 'Discovery', color: 'bg-orange-500/20 text-orange-400', icon: '✨' },
    'APP': { name: 'App', color: 'bg-indigo-500/20 text-indigo-400', icon: '📱' },
  };

  const cfg = config[type] || { name: type || 'Campaign', color: 'bg-zinc-500/20 text-zinc-400', icon: '📊' };

  return (
    <span className={`${cfg.color} px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1`}>
      <span>{cfg.icon}</span>
      <span>{cfg.name}</span>
    </span>
  );
};

const ActionBadge = ({ text, type = 'warning' }) => {
  const colors = {
    critical: 'bg-red-500/10 text-red-400 border-red-500/20',
    warning: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    info: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    success: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
  };

  return (
    <span className={`${colors[type]} px-2 py-1 rounded-md text-xs font-medium border`}>
      {text}
    </span>
  );
};

// Objective-specific KPI configurations for expanded campaign view
const getGoogleCampaignKPIs = (campaign) => {
  const campaign_type = campaign.objective || 'SEARCH';
  const config = GOOGLE_CAMPAIGN_CONFIG[campaign_type] || GOOGLE_CAMPAIGN_CONFIG['SEARCH'];
  
  const conversions = campaign.conversions || 0;
  const all_conversions = campaign.all_conversions || conversions;
  const clicks = campaign.clicks || 0;
  const spend = campaign.spend || 0;
  const video_views = campaign.video_views || 0;
  const roas = campaign.roas || 0;
  const ctr = campaign.ctr || 0;
  
  // Calculate derived metrics
  const totalResults = conversions;
  const cpa = totalResults > 0 ? spend / totalResults : 0;
  const convRate = clicks > 0 ? (totalResults / clicks * 100) : 0;
  const cpc = clicks > 0 ? spend / clicks : 0;
  const cpm = campaign.cpm || 0;
  
  // Helper to determine status
  const getMetricStatus = (value, thresholds, higherIsBetter = true) => {
    if (!thresholds) return 'normal';
    if (higherIsBetter) {
      if (value >= thresholds.good) return 'good';
      if (value >= thresholds.warning) return 'warning';
      return value > 0 ? 'critical' : 'normal';
    } else {
      if (value <= thresholds.good) return 'good';
      if (value <= thresholds.warning) return 'warning';
      return value > 0 ? 'critical' : 'normal';
    }
  };

  // Search campaigns
  if (campaign_type === 'SEARCH') {
    return [
      { label: 'Custom Conv', value: formatNumber(conversions), status: conversions > 0 ? 'good' : 'warning', tooltip: 'Advertiser-tracked conversions' },
      { label: 'CPA', value: formatCurrency(cpa), status: getMetricStatus(cpa, config.kpis.cpa, false) },
      { label: 'Conv Rate', value: `${convRate.toFixed(1)}%`, status: getMetricStatus(convRate, config.kpis.conv_rate) },
      { label: 'CTR', value: `${ctr.toFixed(2)}%`, status: getMetricStatus(ctr, config.kpis.ctr) },
      { label: 'CPC', value: formatCurrency(cpc), status: cpc <= 15 ? 'good' : cpc <= 30 ? 'warning' : 'critical' },
      { label: 'Clicks', value: formatNumber(clicks), status: 'normal' },
    ];
  }

  // Performance Max campaigns
  if (campaign_type === 'PERFORMANCE_MAX') {
    return [
      { label: 'Custom Conv', value: formatNumber(conversions), status: conversions > 0 ? 'good' : 'warning' },
      { label: 'All Conv', value: formatNumber(all_conversions), status: all_conversions > conversions ? 'good' : 'normal', tooltip: 'Includes Google-hosted conversions' },
      { label: 'ROAS', value: `${roas.toFixed(2)}x`, status: getMetricStatus(roas, config.kpis.roas) },
      { label: 'CPA', value: formatCurrency(cpa), status: getMetricStatus(cpa, config.kpis.cpa, false) },
      { label: 'CTR', value: `${ctr.toFixed(2)}%`, status: getMetricStatus(ctr, { good: 2, warning: 1 }) },
      { label: 'CPC', value: formatCurrency(cpc), status: cpc <= 15 ? 'good' : cpc <= 30 ? 'warning' : 'critical' },
    ];
  }

  // Display campaigns
  if (campaign_type === 'DISPLAY') {
    const viewThroughConv = all_conversions - conversions;  // Approximation
    return [
      { label: 'Click Conv', value: formatNumber(conversions), status: conversions > 0 ? 'good' : 'warning', tooltip: 'Direct click conversions' },
      { label: 'View-through', value: formatNumber(viewThroughConv > 0 ? viewThroughConv : 0), status: viewThroughConv > 0 ? 'good' : 'normal', tooltip: 'View-through conversions' },
      { label: 'CPA', value: formatCurrency(cpa), status: getMetricStatus(cpa, config.kpis.cpa, false) },
      { label: 'CPM', value: formatCurrency(cpm), status: getMetricStatus(cpm, config.kpis.cpm, false) },
      { label: 'CTR', value: `${ctr.toFixed(2)}%`, status: getMetricStatus(ctr, config.kpis.ctr) },
      { label: 'CPC', value: formatCurrency(cpc), status: 'normal' },
    ];
  }

  // Video campaigns
  if (campaign_type === 'VIDEO') {
    const cpv = video_views > 0 ? spend / video_views : 0;
    const viewRate = campaign.impressions > 0 ? (video_views / campaign.impressions * 100) : 0;
    return [
      { label: 'Views', value: formatNumber(video_views), status: video_views > 0 ? 'good' : 'warning' },
      { label: 'CPV', value: formatCurrency(cpv), status: getMetricStatus(cpv, config.kpis.cpv, false) },
      { label: 'View Rate', value: `${viewRate.toFixed(1)}%`, status: getMetricStatus(viewRate, config.kpis.view_rate) },
      { label: 'Conversions', value: formatNumber(conversions), status: conversions > 0 ? 'good' : 'normal' },
      { label: 'CTR', value: `${ctr.toFixed(2)}%`, status: getMetricStatus(ctr, config.kpis.ctr) },
      { label: 'CPM', value: formatCurrency(cpm), status: 'normal' },
    ];
  }

  // Shopping campaigns
  if (campaign_type === 'SHOPPING') {
    return [
      { label: 'Purchases', value: formatNumber(conversions), status: conversions > 0 ? 'good' : 'warning' },
      { label: 'ROAS', value: `${roas.toFixed(2)}x`, status: getMetricStatus(roas, config.kpis.roas) },
      { label: 'CPA', value: formatCurrency(cpa), status: getMetricStatus(cpa, config.kpis.cpa, false) },
      { label: 'CTR', value: `${ctr.toFixed(2)}%`, status: getMetricStatus(ctr, config.kpis.ctr) },
      { label: 'CPC', value: formatCurrency(cpc), status: cpc <= 10 ? 'good' : cpc <= 25 ? 'warning' : 'critical' },
      { label: 'Conv Value', value: formatCurrency(campaign.conversion_value || 0), status: 'normal' },
    ];
  }

  // App campaigns
  if (campaign_type === 'APP') {
    const cpi = conversions > 0 ? spend / conversions : 0;
    const installRate = clicks > 0 ? (conversions / clicks * 100) : 0;
    return [
      { label: 'Installs', value: formatNumber(conversions), status: conversions > 0 ? 'good' : 'warning' },
      { label: 'CPI', value: formatCurrency(cpi), status: getMetricStatus(cpi, config.kpis.cpi, false) },
      { label: 'Install Rate', value: `${installRate.toFixed(1)}%`, status: getMetricStatus(installRate, config.kpis.install_rate) },
      { label: 'CTR', value: `${ctr.toFixed(2)}%`, status: getMetricStatus(ctr, config.kpis.ctr) },
      { label: 'CPC', value: formatCurrency(cpc), status: 'normal' },
      { label: 'Clicks', value: formatNumber(clicks), status: 'normal' },
    ];
  }

  // Discovery campaigns
  if (campaign_type === 'DISCOVERY') {
    return [
      { label: 'Conversions', value: formatNumber(conversions), status: conversions > 0 ? 'good' : 'warning' },
      { label: 'CPA', value: formatCurrency(cpa), status: getMetricStatus(cpa, config.kpis.cpa, false) },
      { label: 'CTR', value: `${ctr.toFixed(2)}%`, status: getMetricStatus(ctr, config.kpis.ctr) },
      { label: 'CPC', value: formatCurrency(cpc), status: cpc <= 20 ? 'good' : cpc <= 40 ? 'warning' : 'critical' },
      { label: 'Clicks', value: formatNumber(clicks), status: 'normal' },
      { label: 'CPM', value: formatCurrency(cpm), status: 'normal' },
    ];
  }

  // Default (unknown campaign type)
  return [
    { label: 'Conversions', value: formatNumber(conversions), status: conversions > 0 ? 'good' : 'warning' },
    { label: 'CPA', value: formatCurrency(cpa), status: cpa > 0 && cpa <= 500 ? 'good' : cpa <= 1000 ? 'warning' : 'critical' },
    { label: 'CTR', value: `${ctr.toFixed(2)}%`, status: ctr >= 2 ? 'good' : ctr >= 1 ? 'warning' : 'critical' },
    { label: 'CPC', value: formatCurrency(cpc), status: 'normal' },
    { label: 'CPM', value: formatCurrency(cpm), status: 'normal' },
    { label: 'Clicks', value: formatNumber(clicks), status: 'normal' },
  ];
};

const GoogleIcon = ({ size = 'md' }) => {
  const sizes = { sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-6 h-6' };
  return (
    <svg className={sizes[size]} viewBox="0 0 24 24" fill="none">
      <path d="M23.766 12.2764C23.766 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.766 15.9274 23.766 12.2764Z" fill="#4285F4" />
      <path d="M12.2401 24.0008C15.4766 24.0008 18.2059 22.9382 20.1945 21.1039L16.3275 18.1055C15.2517 18.8375 13.8627 19.252 12.2445 19.252C9.11388 19.252 6.45946 17.1399 5.50705 14.3003H1.5166V17.3912C3.55371 21.4434 7.7029 24.0008 12.2401 24.0008Z" fill="#34A853" />
      <path d="M5.50253 14.3003C5.00236 12.8099 5.00236 11.1961 5.50253 9.70575V6.61481H1.51649C-0.18551 10.0056 -0.18551 14.0004 1.51649 17.3912L5.50253 14.3003Z" fill="#FBBC05" />
      <path d="M12.2401 4.74966C13.9509 4.7232 15.6044 5.36697 16.8434 6.54867L20.2695 3.12262C18.1001 1.0855 15.2208 -0.0344664 12.2401 0.000808666C7.7029 0.000808666 3.55371 2.55822 1.5166 6.61481L5.50264 9.70575C6.45064 6.86173 9.10947 4.74966 12.2401 4.74966Z" fill="#EA4335" />
    </svg>
  );
};

// AI Analysis Panel
const CampaignRow = ({ campaign, isExpanded, onToggle }) => {
  const scoreData = calculateCampaignScore(campaign);
  const campaign_type = campaign.objective || 'SEARCH';
  const config = GOOGLE_CAMPAIGN_CONFIG[campaign_type] || GOOGLE_CAMPAIGN_CONFIG['SEARCH'];
  
  // Get primary and cost metrics based on campaign type
  const conversions = campaign.conversions || 0;
  const video_views = campaign.video_views || 0;
  const all_conversions = campaign.all_conversions || conversions;
  const totalResults = conversions;
  
  // Determine primary metric display
  let primaryMetric, primaryLabel;
  if (campaign_type === 'VIDEO') {
    primaryMetric = video_views;
    primaryLabel = 'Views';
  } else {
    primaryMetric = totalResults;
    primaryLabel = 'Conv';
  }
  
  const cpa = totalResults > 0 ? campaign.spend / totalResults : 0;
  
  // Get objective-specific KPIs for expanded view
  const campaignKPIs = getGoogleCampaignKPIs(campaign);

  return (
    <div className={`bg-[#0D0D0F] rounded-xl border transition-colors ${
      scoreData.status === 'good' ? 'border-emerald-500/20 hover:border-emerald-500/40' :
      scoreData.status === 'warning' ? 'border-amber-500/20 hover:border-amber-500/40' :
      scoreData.status === 'critical' ? 'border-red-500/20 hover:border-red-500/40' :
      'border-white/10 hover:border-white/20'
    }`}>
      <div className="p-4 cursor-pointer" onClick={onToggle}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <CampaignTypeBadge type={campaign.objective} />
              <ScoreBadge score={scoreData.score} status={scoreData.status} size="sm" />
              {campaign.status !== 'ACTIVE' && campaign.status !== 'ENABLED' && (
                <span className="px-2 py-0.5 bg-zinc-500/20 text-zinc-400 rounded-md text-[10px] font-bold">
                  {campaign.status}
                </span>
              )}
            </div>
            <h4 className="text-sm font-semibold text-white truncate">{campaign.name}</h4>
            <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500">
              <span>{formatNumber(campaign.impressions)} impr</span>
              <span>•</span>
              <span>{formatNumber(campaign.clicks)} clicks</span>
              <span>•</span>
              <span>CTR {campaign.ctr?.toFixed(2) || 0}%</span>
            </div>
          </div>
          <div className="flex items-center gap-4 text-right shrink-0">
            <div className="text-center min-w-[60px]">
              <p className="text-[10px] text-zinc-500 uppercase">{primaryLabel}</p>
              <p className="text-lg font-bold text-emerald-400 font-mono">{formatNumber(primaryMetric)}</p>
            </div>
            <div className="text-center min-w-[60px]">
              <p className="text-[10px] text-zinc-500 uppercase">{config.cost_label}</p>
              <p className="text-lg font-bold text-white font-mono">{formatCurrency(cpa)}</p>
            </div>
            <div className="text-center min-w-[70px]">
              <p className="text-[10px] text-zinc-500 uppercase">Spend</p>
              <p className="text-lg font-bold text-white font-mono">{formatCurrency(campaign.spend)}</p>
            </div>
            <svg className={`w-5 h-5 text-zinc-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 pt-2 border-t border-white/5 animate-fade-in">
          {scoreData.reasons.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {scoreData.reasons.map((reason, i) => (
                <ActionBadge 
                  key={i} 
                  text={reason} 
                  type={reason.includes('Excellent') || reason.includes('Strong') || reason.includes('Low CPC') || reason.includes('form leads') ? 'success' : 
                        reason.includes('High') || reason.includes('No ') || reason.includes('Low ROAS') ? 'critical' : 'warning'} 
                />
              ))}
            </div>
          )}

          {/* Objective-specific KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:cols-6 gap-3">
            {campaignKPIs.map((kpi, idx) => (
              <div 
                key={idx} 
                className={`rounded-lg p-3 ${
                  kpi.status === 'critical' ? 'bg-red-500/10' :
                  kpi.status === 'warning' ? 'bg-amber-500/10' :
                  kpi.status === 'good' ? 'bg-emerald-500/10' :
                  'bg-white/5'
                }`}
                title={kpi.tooltip || ''}
              >
                <p className="text-[10px] text-zinc-500 uppercase">{kpi.label}</p>
                <p className={`text-base font-bold font-mono ${
                  kpi.status === 'critical' ? 'text-red-400' :
                  kpi.status === 'warning' ? 'text-amber-400' :
                  kpi.status === 'good' ? 'text-emerald-400' :
                  'text-white'
                }`}>
                  {kpi.value}
                </p>
              </div>
            ))}
          </div>
          
          {/* Conversion breakdown for complex types */}
          {(all_conversions > conversions || (campaign.conversion_actions && Object.keys(campaign.conversion_actions).length > 0)) && (
            <div className="mt-3 p-3 bg-blue-500/5 rounded-lg border border-blue-500/20">
              <p className="text-[10px] text-blue-400 uppercase font-bold mb-2">Conversion Breakdown</p>
              
              {/* Summary stats */}
              <div className="flex flex-wrap gap-4 text-xs mb-3">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                  <span className="text-zinc-400">Custom Conversions:</span>
                  <span className="text-white font-bold">{formatNumber(conversions)}</span>
                </div>
                {all_conversions > conversions && (
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                    <span className="text-zinc-400">All Conversions:</span>
                    <span className="text-white font-bold">{formatNumber(all_conversions)}</span>
                  </div>
                )}
              </div>
              
              {/* Detailed conversion actions breakdown */}
              {campaign.conversion_actions && campaign.conversion_actions.actions && campaign.conversion_actions.actions.length > 0 && (
                <div className="border-t border-blue-500/20 pt-3">
                  <p className="text-[10px] text-zinc-500 uppercase mb-2">Conversion Actions Detail</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {campaign.conversion_actions.actions.filter(action => {
                      // Filter out phone-related conversions from display
                      const isPhoneRelated = action.name.toLowerCase().includes('phone') || action.name.toLowerCase().includes('call');
                      return !isPhoneRelated;
                    }).map((action, idx) => {
                      const isFormRelated = action.name.toLowerCase().includes('form') || action.name.toLowerCase().includes('submit');
                      const isWhatsApp = action.name.toLowerCase().includes('whatsapp') || action.name.toLowerCase().includes('chat');
                      
                      const actionColor = isFormRelated ? 'bg-purple-500/10 border-purple-500/30 text-purple-400' :
                                         isWhatsApp ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' :
                                         'bg-zinc-500/10 border-zinc-500/30 text-zinc-400';
                      
                      const icon = isFormRelated ? 'F' : isWhatsApp ? 'W' : 'C';
                      
                      return (
                        <div key={idx} className={`${actionColor} rounded-lg p-2 border text-xs`}>
                          <div className="flex items-center justify-between">
                            <span className="truncate flex-1" title={action.name}>
                              {icon} {action.name.length > 30 ? action.name.substring(0, 30) + '...' : action.name}
                            </span>
                            <span className="font-bold ml-2">{formatNumber(action.conversions)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Aggregated by type - excluding phone calls */}
                  {(campaign.conversion_actions.form_submissions > 0 ||
                    campaign.conversion_actions.website_conversions > 0) && (
                    <div className="mt-3 pt-3 border-t border-blue-500/10">
                      <p className="text-[10px] text-zinc-500 uppercase mb-2">Aggregated by Type</p>
                      <div className="flex flex-wrap gap-3">
                        {campaign.conversion_actions.form_submissions > 0 && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-purple-500/10 rounded-md">
                            <span className="text-purple-400 text-xs">Form Submissions:</span>
                            <span className="text-white font-bold text-xs">{campaign.conversion_actions.form_submissions}</span>
                          </div>
                        )}
                        {campaign.conversion_actions.website_conversions > 0 && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-emerald-500/10 rounded-md">
                            <span className="text-emerald-400 text-xs">WhatsApp/Chat:</span>
                            <span className="text-white font-bold text-xs">{campaign.conversion_actions.website_conversions}</span>
                          </div>
                        )}
                        {campaign.conversion_actions.other_conversions > 0 && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-zinc-500/10 rounded-md">
                            <span className="text-zinc-400 text-xs">Other:</span>
                            <span className="text-white font-bold text-xs">{campaign.conversion_actions.other_conversions}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Client Card Component
const ClientCard = ({ client, campaigns, isExpanded, onToggle, onAnalyze, addToast }) => {
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const clientScore = calculateClientScore(campaigns);

  // Use account-level metrics if available (from API), fallback to campaign aggregation
  const activeCampaigns = campaigns.filter(c => c.spend > 0);
  const dormantCampaigns = campaigns.filter(c => c.spend === 0 || c.spend === null);

  // Prefer account-level metrics from API
  const totalSpend = client.metrics?.spend || activeCampaigns.reduce((sum, c) => sum + (c.spend || 0), 0);
  
  // Calculate segmented conversions for this client (excluding phone calls)
  let totalFormLeads = 0;
  
  campaigns.forEach(camp => {
    const baseConversions = camp.conversions || camp.leads || 0;
    
    if (camp.conversion_actions) {
      const ca = camp.conversion_actions;
      totalFormLeads += (ca.form_submissions || 0);
      
      // If no form_submissions tracked, use base conversions
      if (!ca.form_submissions && baseConversions > 0) {
        const categorized = (ca.form_submissions || 0) + (ca.website_conversions || 0) + (ca.other_conversions || 0);
        if (categorized < baseConversions) {
          totalFormLeads += (baseConversions - categorized);
        }
      }
    } else {
      totalFormLeads += baseConversions;
    }
  });
  
  const totalConversions = totalFormLeads;
  const avgCPA = totalConversions > 0 ? totalSpend / totalConversions : 0;

  // Count campaigns with spend for display
  const campaignsWithSpend = campaigns.filter(c => c.spend && c.spend > 0);
  const goodCount = campaignsWithSpend.filter(c => calculateCampaignScore(c).status === 'good').length;
  const warningCount = campaignsWithSpend.filter(c => calculateCampaignScore(c).status === 'warning').length;
  const criticalCount = campaignsWithSpend.filter(c => calculateCampaignScore(c).status === 'critical').length;

  // Team assignment info
  const assignedMarketerName = client.assigned_marketer_name;
  const teamLeadName = client.team_lead_name;

  return (
    <div className={`bg-[#09090B] rounded-2xl border transition-colors ${
      isExpanded ? 'border-[#4285F4]/50 ring-2 ring-[#4285F4]/20' :
      clientScore.status === 'good' ? 'border-emerald-500/30 hover:border-emerald-500/50' :
      clientScore.status === 'warning' ? 'border-amber-500/30 hover:border-amber-500/50' :
      clientScore.status === 'critical' ? 'border-red-500/30 hover:border-red-500/50' :
      'border-white/10 hover:border-white/20'
    }`}>
      <div className="p-5 cursor-pointer" onClick={onToggle}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg shadow-white/10">
              <GoogleIcon size="lg" />
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-bold text-white text-lg">{client.name}</h3>
                <ScoreBadge score={clientScore.score} status={clientScore.status} />
                {/* Team Assignment Tags */}
                {assignedMarketerName && (
                  <span className="bg-emerald-500 text-black px-2.5 py-1 rounded-lg text-xs font-bold ring-2 ring-emerald-400 flex items-center gap-1 ml-2">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {assignedMarketerName.trim()}
                  </span>
                )}
                {teamLeadName && teamLeadName !== 'None' && (
                  <span className="bg-[#6366f1] text-black px-2.5 py-1 rounded-lg text-xs font-bold ring-2 ring-cyan-400 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    {teamLeadName}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1 text-xs text-zinc-500">
                <span>{campaignsWithSpend.length > 0 ? `${campaignsWithSpend.length} active campaigns` : `${campaigns.length} campaigns`}</span>
                {dormantCampaigns.length > 0 && campaignsWithSpend.length > 0 && (
                  <span className="text-zinc-600">• {dormantCampaigns.length} dormant</span>
                )}
                {(goodCount > 0 || warningCount > 0 || criticalCount > 0) && <span className="text-zinc-700">|</span>}
                {goodCount > 0 && <span className="text-emerald-400">{goodCount} good</span>}
                {warningCount > 0 && <span className="text-amber-400">{warningCount} warning</span>}
                {criticalCount > 0 && <span className="text-red-400">{criticalCount} critical</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-4">
              <div className="text-right">
                <p className="text-xs text-zinc-500 uppercase">Spend</p>
                <p className="text-lg font-bold text-white font-mono">{formatCurrency(totalSpend)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-zinc-500 uppercase">Total Results</p>
                <p className="text-lg font-bold text-emerald-400 font-mono">{formatNumber(totalConversions)}</p>
              </div>
              {/* Segmented breakdown */}
              <div className="text-right border-l border-white/10 pl-4">
                <p className="text-[10px] text-zinc-600 uppercase flex items-center gap-1 justify-end">
                  <span className="text-purple-400">Contacts</span>
                </p>
                <p className="text-base font-bold text-purple-400 font-mono">{formatNumber(totalFormLeads)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-zinc-500 uppercase">Avg CPA</p>
                <p className="text-lg font-bold text-white font-mono">{formatCurrency(avgCPA)}</p>
              </div>
            </div>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
              isExpanded ? 'bg-[#4285F4]/20 text-[#4285F4]' : 'bg-white/5 text-zinc-500'
            }`}>
              <svg className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2 mt-4 md:hidden">
          <div className="bg-white/5 rounded-lg p-2 text-center">
            <p className="text-[10px] text-zinc-500">Spend</p>
            <p className="text-sm font-bold text-white font-mono">{formatCurrency(totalSpend)}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-2 text-center">
            <p className="text-[10px] text-zinc-500">Total</p>
            <p className="text-sm font-bold text-emerald-400 font-mono">{formatNumber(totalConversions)}</p>
          </div>
          <div className="bg-purple-500/10 rounded-lg p-2 text-center">
            <p className="text-[10px] text-purple-400">Contacts</p>
            <p className="text-sm font-bold text-purple-400 font-mono">{formatNumber(totalFormLeads)}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-2 text-center">
            <p className="text-[10px] text-zinc-500">CPA</p>
            <p className="text-sm font-bold text-white font-mono">{formatCurrency(avgCPA)}</p>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-white/10 p-5 animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-bold text-white flex items-center gap-2">
              <svg className="w-4 h-4 text-[#4285F4]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Campaign Performance
            </h4>
            <button
              onClick={(e) => { e.stopPropagation(); onAnalyze(); }}
              className="px-3 py-1.5 bg-gradient-to-r from-[#4285F4] to-[#34A853] text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 hover:opacity-90 transition-colors"
            >
              <Zap className="w-4 h-4" />
              AI Analysis
            </button>
          </div>

          <div className="space-y-3 max-h-[500px] overflow-y-auto custom-scrollbar pr-1">
            {activeCampaigns
              .sort((a, b) => (b.spend || 0) - (a.spend || 0))
              .map(campaign => (
                <CampaignRow
                  key={campaign.id}
                  campaign={campaign}
                  isExpanded={expandedCampaign === campaign.id}
                  onToggle={() => setExpandedCampaign(expandedCampaign === campaign.id ? null : campaign.id)}
                />
              ))}
          </div>

          {activeCampaigns.length === 0 && (
            <div className="text-center py-8 text-zinc-500">
              <p>No active campaigns with spend</p>
            </div>
          )}

          <div className="mt-4 border-t border-[#27272a] pt-4">
            <p className="text-xs text-[#71717a] uppercase tracking-wider mb-3" style={{fontFamily:'Manrope,sans-serif'}}>AI Optimization</p>
            <GoogleOptimizationPanel
              accountId={client.id}
              accountName={client.name}
              addToast={addToast}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Inactive Account Card - FIXED to show account name
const InactiveAccountCard = ({ account }) => (
  <InactiveAccountCardBase
    account={account}
    idPrefix="google_"
    logoWrapperClassName="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg shadow-white/10 opacity-50"
    logo={<GoogleIcon size="lg" />}
    buttonClassName="bg-white/10 hover:bg-white/20 text-white"
    buttonLabel="Open in Google Ads"
    buildUrl={(id) => `https://ads.google.com/aw/overview?ocid=${id}`}
  />
);

const GooglePerformance = ({ addToast }) => {
  const {
    dashboardData, loading, inactiveAccounts,
    fetchAllData, fetchAlerts,
    expandedClient, setExpandedClient,
    activeTab, setActiveTab,
    filterStatus, setFilterStatus,
    timeframe, handleTimeframeChange,
    accountSearch, setAccountSearch,
    aiAnalysisClient, setAiAnalysisClient,
    healthData,
    alerts, alertsUnreadCount, markAlertAsRead, resolveAlert,
    aiInsights, aiInsightsLoading, aiInsightsError, fetchAiInsights,
    platformClients, filteredClients,
  } = usePerformanceData({
    platform: 'GOOGLE',
    label: 'Google',
    inactiveKey: 'google_inactive',
    fallbackRanges: FALLBACK_RANGES,
    scoreClient: calculateClientScore,
    addToast,
  });

  const summaryStats = useMemo(() => {
    const totalSpend = platformClients.reduce((sum, c) => sum + (c.metrics?.spend || 0), 0);
    
    // Calculate segmented conversions (Form Leads, Website/Chat Conversions, etc.) - excluding phone calls
    let totalFormLeads = 0;
    let totalWebsiteConversions = 0;
    let totalOtherConversions = 0;
    
    platformClients.forEach(client => {
      const campaigns = client.campaigns || [];
      campaigns.forEach(camp => {
        // Base conversions (advertiser-tracked)
        const baseConversions = camp.conversions || camp.leads || 0;
        
        // Get detailed breakdown from conversion_actions if available
        if (camp.conversion_actions) {
          const ca = camp.conversion_actions;
          totalFormLeads += (ca.form_submissions || 0);
          totalWebsiteConversions += (ca.website_conversions || 0);
          totalOtherConversions += (ca.other_conversions || 0);
          
          // If no form_submissions tracked, assume base conversions are forms
          if (!ca.form_submissions && baseConversions > 0) {
            // Only add if not already categorized
            const categorized = (ca.form_submissions || 0) + (ca.website_conversions || 0) + (ca.other_conversions || 0);
            if (categorized < baseConversions) {
              totalFormLeads += (baseConversions - categorized);
            }
          }
        } else {
          // No detailed breakdown - use base conversions
          totalFormLeads += baseConversions;
        }
      });
    });
    
    // Total all conversions (Form + Website/Chat + Other) - excluding phone calls
    const totalConversions = totalFormLeads + totalWebsiteConversions + totalOtherConversions;
    
    const totalClicks = platformClients.reduce((sum, c) => sum + (c.metrics?.clicks || 0), 0);
    const avgCPA = totalConversions > 0 ? totalSpend / totalConversions : 0;
    
    const clientsWithScores = platformClients.map(c => ({
      ...c,
      score: calculateClientScore(c.campaigns || [])
    }));
    
    const goodClients = clientsWithScores.filter(c => c.score.status === 'good').length;
    const warningClients = clientsWithScores.filter(c => c.score.status === 'warning').length;
    const criticalClients = clientsWithScores.filter(c => c.score.status === 'critical').length;
    const needsAttention = warningClients + criticalClients;
    
    // Find top performer (highest score)
    const topPerformer = clientsWithScores.length > 0 
      ? clientsWithScores.reduce((best, c) => c.score.score > best.score.score ? c : best)
      : null;
    
    // Find worst performer (lowest score with spend > 0)
    const activeClients = clientsWithScores.filter(c => c.metrics?.spend > 0);
    const worstPerformer = activeClients.length > 0
      ? activeClients.reduce((worst, c) => c.score.score < worst.score.score ? c : worst)
      : null;

    return { 
      totalSpend, 
      totalConversions,
      // Segmented conversion data (excluding phone calls)
      totalFormLeads,
      totalWebsiteConversions,
      totalOtherConversions,
      // Other stats
      totalClicks, 
      avgCPA,
      goodClients, 
      warningClients, 
      criticalClients,
      needsAttention,
      topPerformer,
      worstPerformer
    };
  }, [platformClients]);

  // Helper function for campaign type names
  const getCampaignTypeShortName = (type) => {
    const mapping = {
      'SEARCH': 'Search',
      'DISPLAY': 'Display',
      'VIDEO': 'Video',
      'SHOPPING': 'Shopping',
      'PERFORMANCE_MAX': 'PMax',
      'DISCOVERY': 'Discovery',
      'APP': 'App',
    };
    return mapping[type] || type || 'Other';
  };

  // State for collapsible insight categories
  const [expandedCategories, setExpandedCategories] = useState({
    aiInsights: true,  // AI Insights expanded by default
    immediate: true,
    trends: true,
    conversion: false,
    budget: false,
    efficiency: false
  });

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  // Advanced insights data for the goldmine section - 18 TOTAL LISTS FOR GOOGLE ADS
  const insightsData = useMemo(() => {
    const allCampaigns = platformClients.flatMap(c => c.campaigns || []).filter(camp => camp.spend > 0);
    
    // Helper to get campaign with client info
    const enrichCampaign = (camp) => {
      const client = platformClients.find(c => c.campaigns?.some(cc => cc.id === camp.id));
      const conversions = camp.conversions || camp.leads || 0;
      const cpa = conversions > 0 ? camp.spend / conversions : Infinity;
      const cpc = camp.clicks > 0 ? camp.spend / camp.clicks : 0;
      const cpm = camp.impressions > 0 ? (camp.spend / camp.impressions) * 1000 : 0;
      const convRate = camp.clicks > 0 ? (conversions / camp.clicks) * 100 : 0;
      const isSearch = camp.objective === 'SEARCH';
      const isPMax = camp.objective === 'PERFORMANCE_MAX';
      const isDisplay = camp.objective === 'DISPLAY';
      const isVideo = camp.objective === 'VIDEO';
      const isShopping = camp.objective === 'SHOPPING';
      return { 
        ...camp, 
        cpa, 
        conversions, 
        cpc, 
        cpm,
        convRate,
        clientName: client?.name,
        clientId: client?.id,
        isSearch,
        isPMax,
        isDisplay,
        isVideo,
        isShopping,
        type: getCampaignTypeShortName(camp.objective)
      };
    };

    const enrichedCampaigns = allCampaigns.map(enrichCampaign);
    
    // Calculate averages for comparison
    const avgCPA = enrichedCampaigns.filter(c => c.cpa < Infinity && c.conversions > 0).reduce((sum, c) => sum + c.cpa, 0) / 
                   (enrichedCampaigns.filter(c => c.cpa < Infinity && c.conversions > 0).length || 1);
    const avgCPC = enrichedCampaigns.filter(c => c.cpc > 0).reduce((sum, c) => sum + c.cpc, 0) / 
                   (enrichedCampaigns.filter(c => c.cpc > 0).length || 1);
    const avgCPM = enrichedCampaigns.filter(c => c.cpm > 0).reduce((sum, c) => sum + c.cpm, 0) / 
                   (enrichedCampaigns.filter(c => c.cpm > 0).length || 1);
    const avgCTR = enrichedCampaigns.filter(c => c.ctr > 0).reduce((sum, c) => sum + c.ctr, 0) / 
                   (enrichedCampaigns.filter(c => c.ctr > 0).length || 1);
    const avgConvRate = enrichedCampaigns.filter(c => c.convRate > 0).reduce((sum, c) => sum + c.convRate, 0) / 
                        (enrichedCampaigns.filter(c => c.convRate > 0).length || 1);

    // ==================== ORIGINAL 7 LISTS ====================
    
    // 1. SCALE OPPORTUNITIES - Low CPA, low spend, ready to scale
    const scaleOpportunities = enrichedCampaigns
      .filter(camp => camp.cpa < 300 && camp.spend < 3000 && camp.conversions >= 3)
      .sort((a, b) => a.cpa - b.cpa)
      .slice(0, 5);

    // 2. BUDGET DRAINS - High spend but poor results
    const budgetDrains = enrichedCampaigns
      .filter(camp => (camp.cpa > 800 || camp.conversions === 0) && camp.spend >= 500)
      .sort((a, b) => b.spend - a.spend)
      .slice(0, 5);

    // 3. QUALITY SCORE ISSUES - Low CTR campaigns (Google specific)
    const qualityScoreIssues = enrichedCampaigns
      .filter(camp => camp.ctr < 1.5 && camp.impressions > 5000 && (camp.isSearch || camp.isPMax))
      .sort((a, b) => a.ctr - b.ctr)
      .slice(0, 8);

    // 4. TOP PERFORMERS (Lowest CPA)
    const topEfficient = enrichedCampaigns
      .filter(camp => camp.conversions >= 3 && camp.cpa < Infinity)
      .sort((a, b) => a.cpa - b.cpa)
      .slice(0, 8);

    // 5. QUICK WINS (Low CTR - Needs Ad Copy Refresh)
    const quickWins = enrichedCampaigns
      .filter(camp => camp.ctr < 2 && camp.spend > 500 && camp.isSearch)
      .sort((a, b) => b.spend - a.spend)
      .slice(0, 5);

    // 6. SPEND BY CLIENT
    const spendByClient = platformClients
      .filter(c => c.metrics?.spend > 0)
      .map(c => ({
        name: c.name?.length > 12 ? c.name.substring(0, 12) + '...' : c.name,
        fullName: c.name,
        id: c.id,
        spend: c.metrics?.spend || 0,
        conversions: (c.campaigns || []).reduce((s, camp) => s + (camp.conversions || camp.leads || 0), 0),
        score: calculateClientScore(c.campaigns || []).score
      }))
      .sort((a, b) => b.spend - a.spend)
      .slice(0, 10);

    // 7. CAMPAIGN TYPE PERFORMANCE
    const campaignTypePerf = {};
    enrichedCampaigns.forEach(camp => {
      const type = camp.objective || 'OTHER';
      if (!campaignTypePerf[type]) campaignTypePerf[type] = { spend: 0, conversions: 0, campaigns: 0, clicks: 0 };
      campaignTypePerf[type].spend += camp.spend || 0;
      campaignTypePerf[type].conversions += camp.conversions;
      campaignTypePerf[type].campaigns += 1;
      campaignTypePerf[type].clicks += camp.clicks || 0;
    });
    const campaignTypeData = Object.entries(campaignTypePerf)
      .map(([type, data]) => ({
        type: getCampaignTypeShortName(type),
        fullName: type,
        spend: data.spend,
        conversions: data.conversions,
        campaigns: data.campaigns,
        clicks: data.clicks,
        cpa: data.conversions > 0 ? data.spend / data.conversions : 0,
      }))
      .filter(d => d.spend > 0)
      .sort((a, b) => b.spend - a.spend);

    // ==================== NEW 11 LISTS ====================

    // 8. DECLINING PERFORMERS - CPA increased significantly
    const decliningPerformers = enrichedCampaigns
      .filter(camp => camp.cpa > avgCPA * 1.3 && camp.conversions > 0 && camp.spend > 300)
      .map(camp => ({
        ...camp,
        cpaIncrease: ((camp.cpa - avgCPA) / avgCPA * 100).toFixed(0),
        vsAvg: camp.cpa / avgCPA
      }))
      .sort((a, b) => b.vsAvg - a.vsAvg)
      .slice(0, 5);

    // 9. RISING STARS - Good conversions with improving efficiency
    const risingStars = enrichedCampaigns
      .filter(camp => camp.cpa < avgCPA * 0.7 && camp.conversions >= 5 && camp.spend >= 300)
      .map(camp => ({
        ...camp,
        efficiency: ((avgCPA - camp.cpa) / avgCPA * 100).toFixed(0),
        belowAvg: avgCPA / camp.cpa
      }))
      .sort((a, b) => b.belowAvg - a.belowAvg)
      .slice(0, 5);

    // 10. COST SPIKE ALERT - CPA way above average
    const costSpikeAlerts = enrichedCampaigns
      .filter(camp => camp.cpa > avgCPA * 1.5 && camp.conversions > 0 && camp.spend > 500)
      .map(camp => ({
        ...camp,
        spikePercent: ((camp.cpa - avgCPA) / avgCPA * 100).toFixed(0),
        severity: camp.cpa > avgCPA * 2 ? 'critical' : 'warning'
      }))
      .sort((a, b) => b.cpa - a.cpa)
      .slice(0, 5);

    // 11. CLICK-TO-CONVERSION GAP - High clicks but low conversions (landing page issue)
    const clickToConvGap = enrichedCampaigns
      .filter(camp => {
        return camp.clicks > 100 && camp.convRate < 2 && camp.spend > 300;
      })
      .map(camp => ({
        ...camp,
        potentialConv: Math.round(camp.clicks * 0.05) // 5% benchmark
      }))
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 5);

    // 12. IMPRESSION SHARE OPPORTUNITY - Low CTR but decent spend (Search specific)
    const impressionShareOpp = enrichedCampaigns
      .filter(camp => camp.ctr < avgCTR * 0.7 && camp.conversions >= 2 && camp.impressions > 10000 && camp.isSearch)
      .map(camp => ({
        ...camp,
        ctrGap: (avgCTR - camp.ctr).toFixed(2),
        potentialClicks: Math.round(camp.impressions * avgCTR / 100)
      }))
      .sort((a, b) => b.impressions - a.impressions)
      .slice(0, 5);

    // 13. HIDDEN GEMS - Excellent efficiency, minimal spend (underexploited)
    const hiddenGems = enrichedCampaigns
      .filter(camp => camp.cpa < avgCPA * 0.5 && camp.spend < 1500 && camp.conversions >= 5)
      .map(camp => ({
        ...camp,
        efficiencyRating: (avgCPA / camp.cpa).toFixed(1),
        scalePotential: camp.spend < 500 ? 'High' : 'Medium'
      }))
      .sort((a, b) => a.cpa - b.cpa)
      .slice(0, 5);

    // 14. BUDGET MISALLOCATED - High CPA campaigns getting more budget than efficient ones
    const efficientCampaigns = enrichedCampaigns.filter(c => c.cpa < avgCPA && c.conversions > 0).sort((a, b) => a.cpa - b.cpa);
    const inefficientHighSpend = enrichedCampaigns.filter(c => c.cpa > avgCPA * 1.2 && c.spend > 500);
    const budgetMisallocated = inefficientHighSpend
      .map(camp => {
        const betterOption = efficientCampaigns.find(e => e.clientId !== camp.clientId) || efficientCampaigns[0];
        return {
          ...camp,
          betterAlternative: betterOption?.name,
          betterCPA: betterOption?.cpa,
          wastedAmount: camp.conversions > 0 ? Math.round(camp.spend - (camp.conversions * avgCPA)) : camp.spend
        };
      })
      .filter(c => c.wastedAmount > 100)
      .sort((a, b) => b.wastedAmount - a.wastedAmount)
      .slice(0, 5);

    // 15. PMAX OPPORTUNITIES - Performance Max campaigns to optimize
    const pmaxOpportunities = enrichedCampaigns
      .filter(camp => camp.isPMax && camp.conversions >= 2)
      .map(camp => ({
        ...camp,
        performance: camp.cpa < avgCPA ? 'Above Avg' : camp.cpa < avgCPA * 1.3 ? 'Average' : 'Below Avg'
      }))
      .sort((a, b) => a.cpa - b.cpa)
      .slice(0, 5);

    // 16. SEARCH CAMPAIGN LEADERS - Top performing search campaigns
    const searchLeaders = enrichedCampaigns
      .filter(camp => camp.isSearch && camp.conversions >= 3)
      .map(camp => ({
        ...camp,
        qualityIndicator: camp.ctr > 3 ? 'Excellent' : camp.ctr > 2 ? 'Good' : 'Average'
      }))
      .sort((a, b) => a.cpa - b.cpa)
      .slice(0, 5);

    // 17. CPM WATCH - Unusually high CPMs (audience saturation or competition)
    const cpmWatch = enrichedCampaigns
      .filter(camp => camp.cpm > avgCPM * 1.5 && camp.impressions > 5000)
      .map(camp => ({
        ...camp,
        cpmIncrease: ((camp.cpm - avgCPM) / avgCPM * 100).toFixed(0),
        severity: camp.cpm > avgCPM * 2 ? 'critical' : 'warning'
      }))
      .sort((a, b) => b.cpm - a.cpm)
      .slice(0, 5);

    // 18. CPC OUTLIERS - Abnormal cost-per-click
    const cpcOutliers = enrichedCampaigns
      .filter(camp => camp.cpc > avgCPC * 1.5 && camp.clicks > 50)
      .map(camp => ({
        ...camp,
        cpcIncrease: ((camp.cpc - avgCPC) / avgCPC * 100).toFixed(0),
        potentialSavings: Math.round((camp.cpc - avgCPC) * camp.clicks)
      }))
      .sort((a, b) => b.cpc - a.cpc)
      .slice(0, 5);

    return {
      // Original 7
      scaleOpportunities,
      budgetDrains,
      qualityScoreIssues,
      topEfficient,
      quickWins,
      spendByClient,
      campaignTypeData,
      // New 11
      decliningPerformers,
      risingStars,
      costSpikeAlerts,
      clickToConvGap,
      impressionShareOpp,
      hiddenGems,
      budgetMisallocated,
      pmaxOpportunities,
      searchLeaders,
      cpmWatch,
      cpcOutliers,
      // Metadata
      totalCampaigns: allCampaigns.length,
      avgCPA,
      avgCPC,
      avgCPM,
      avgCTR,
      avgConvRate
    };
  }, [platformClients]);


  if (loading && platformClients.length === 0) {
    return (
      <div className="space-y-6" data-testid="google-performance-loading">
        <div className="h-32 bg-zinc-800 rounded-2xl animate-pulse" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-zinc-800 rounded-xl animate-pulse" />)}
        </div>
        {[...Array(5)].map((_, i) => <div key={i} className="animate-pulse bg-zinc-800 rounded-lg h-16 mb-2" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="google-performance-page">
      {/* Header with Timeframe Selector */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
              <GoogleIcon />
            </div>
            Google Ads Performance
          </h1>
          <p className="text-zinc-500 text-sm mt-1">
            Sorted by spend (highest first) • {platformClients.length} active accounts
            {dashboardData?._fallback_range && (
              <span className="ml-2 text-yellow-500/80 text-xs">(showing {dashboardData._fallback_range.replace('_', ' ')} — syncing recent data)</span>
            )}
          </p>
        </div>
        
        {/* Timeframe Selector */}
        <div className="flex items-center gap-2">
          <select
            value={timeframe}
            onChange={(e) => handleTimeframeChange(e.target.value)}
            className="bg-[#18181B] text-white border border-white/10 rounded-xl px-4 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#4285F4]/50"
            data-testid="timeframe-selector"
          >
            {TIMEFRAME_OPTIONS.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
          <button
            onClick={() => fetchAllData()}
            className="px-4 py-2.5 bg-gradient-to-r from-[#4285F4] to-[#34A853] text-white rounded-xl font-semibold text-sm hover:opacity-90 transition-colors shadow-lg shadow-[#4285F4]/20 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-[#18181B] p-1 rounded-xl border border-white/10 w-fit overflow-x-auto">
        <button
          onClick={() => setActiveTab('performance')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'performance'
              ? 'bg-gradient-to-r from-[#4285F4] to-[#34A853] text-white'
              : 'text-zinc-400 hover:text-white'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <span>Performance</span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] ${activeTab === 'performance' ? 'bg-white/20' : 'bg-white/10'}`}>
            {platformClients.length}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('health')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'health'
              ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white'
              : 'text-zinc-400 hover:text-white'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <span>Health</span>
          {healthData && (healthData.warning?.length > 0 || healthData.critical?.length > 0) && (
            <span className={`px-1.5 py-0.5 rounded text-[10px] ${activeTab === 'health' ? 'bg-white/20' : 'bg-amber-500/50 text-white'}`}>
              {(healthData.warning?.length || 0) + (healthData.critical?.length || 0)}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('alerts')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'alerts'
              ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
              : 'text-zinc-400 hover:text-white'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <span>Alerts</span>
          {alertsUnreadCount > 0 && (
            <span className={`px-1.5 py-0.5 rounded text-[10px] animate-pulse ${activeTab === 'alerts' ? 'bg-white/20' : 'bg-red-500 text-white'}`}>
              {alertsUnreadCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('inactive')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'inactive'
              ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white'
              : 'text-zinc-400 hover:text-white'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
          <span>Inactive</span>
          {inactiveAccounts.length > 0 && (
            <span className={`px-1.5 py-0.5 rounded text-[10px] ${activeTab === 'inactive' ? 'bg-white/20' : 'bg-red-500/50 text-white'}`}>
              {inactiveAccounts.length}
            </span>
          )}
        </button>
      </div>

      {/* Performance Tab */}
      {activeTab === 'performance' && (
        <>
          {/* Summary Banner - Action-focused metrics with Segmented Conversions */}
          <div className="bg-gradient-to-r from-[#4285F4]/10 via-[#0D0D0F] to-transparent rounded-2xl border border-[#4285F4]/20 p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {/* Total Spend */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/5">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Total Spend</p>
                <p className="text-3xl font-bold text-white font-mono">{formatCurrency(summaryStats.totalSpend)}</p>
                <p className="text-xs text-zinc-600 mt-1">{platformClients.length} active accounts</p>
              </div>
              
              {/* Total Conversions - All Types */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/5">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Total Results</p>
                <p className="text-3xl font-bold text-emerald-400 font-mono">{formatNumber(summaryStats.totalConversions)}</p>
                <p className="text-xs text-emerald-500/80 mt-1">
                  Avg CPA: {formatCurrency(summaryStats.avgCPA)}
                </p>
              </div>
              
              {/* Clicks */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/5">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Total Clicks</p>
                <p className="text-3xl font-bold text-blue-400 font-mono">{formatNumber(summaryStats.totalClicks)}</p>
                <p className="text-xs text-zinc-600 mt-1">CTR: {insightsData.avgCTR?.toFixed(2)}%</p>
              </div>

              {/* CPC */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/5">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Avg CPC</p>
                <p className="text-3xl font-bold text-amber-400 font-mono">{formatCurrency(insightsData.avgCPC)}</p>
                <p className="text-xs text-zinc-600 mt-1">per click</p>
              </div>
              
              {/* Needs Attention - Critical Action */}
              <div 
                className={`rounded-xl p-4 border cursor-pointer transition-colors ${
                  summaryStats.needsAttention > 0 
                    ? 'bg-red-500/10 border-red-500/30 hover:border-red-500/50' 
                    : 'bg-emerald-500/10 border-emerald-500/30'
                }`}
                onClick={() => summaryStats.needsAttention > 0 && setFilterStatus('critical')}
              >
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  {summaryStats.needsAttention > 0 && (
                    <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse"></span>
                  )}
                  Needs Action
                </p>
                <p className={`text-3xl font-bold font-mono ${summaryStats.needsAttention > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                  {summaryStats.needsAttention}
                </p>
                <p className="text-xs text-zinc-600 mt-1">
                  {summaryStats.criticalClients} critical • {summaryStats.warningClients} warning
                </p>
              </div>
              
              {/* Top Performer */}
              {summaryStats.topPerformer && (
                <div 
                  className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/30 cursor-pointer hover:border-emerald-500/50 transition-colors"
                  onClick={() => setExpandedClient(summaryStats.topPerformer.id)}
                >
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                    <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg> Top Performer
                  </p>
                  <p className="text-lg font-bold text-white truncate" title={summaryStats.topPerformer.name}>
                    {summaryStats.topPerformer.name?.length > 15 
                      ? summaryStats.topPerformer.name.substring(0, 15) + '...' 
                      : summaryStats.topPerformer.name}
                  </p>
                  <p className="text-xs text-emerald-400 mt-1">
                    Score: {summaryStats.topPerformer.score.score} • {formatCurrency(summaryStats.topPerformer.metrics?.spend)} spend
                  </p>
                </div>
              )}
              
              {/* Worst Performer - Action needed */}
              {summaryStats.worstPerformer && summaryStats.worstPerformer.score.score < 60 && (
                <div 
                  className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/30 cursor-pointer hover:border-amber-500/50 transition-colors"
                  onClick={() => setExpandedClient(summaryStats.worstPerformer.id)}
                >
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                    <svg className="w-4 h-4 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg> Needs Review
                  </p>
                  <p className="text-lg font-bold text-white truncate" title={summaryStats.worstPerformer.name}>
                    {summaryStats.worstPerformer.name?.length > 15 
                      ? summaryStats.worstPerformer.name.substring(0, 15) + '...' 
                      : summaryStats.worstPerformer.name}
                  </p>
                  <p className="text-xs text-amber-400 mt-1">
                    Score: {summaryStats.worstPerformer.score.score} • {formatCurrency(summaryStats.worstPerformer.metrics?.spend)} spend
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* === PERFORMANCE INSIGHTS - THE ULTIMATE EDGE FOR GOOGLE ADS === */}
          <div className="space-y-3">
            {/* Main Header with Stats */}
            <div className="bg-gradient-to-r from-[#4285F4]/10 via-[#34A853]/5 to-transparent rounded-2xl border border-[#4285F4]/20 p-5">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#4285F4] to-[#34A853] rounded-xl flex items-center justify-center shadow-lg shadow-[#4285F4]/20">
                    <span className="text-2xl">💎</span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Performance Insights</h2>
                    <p className="text-sm text-zinc-400">Actionable data to dominate Google Ads</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-white/5 rounded-lg px-3 py-2 text-center">
                    <p className="text-lg font-bold text-white">{insightsData.totalCampaigns}</p>
                    <p className="text-[10px] text-zinc-500 uppercase">Campaigns</p>
                  </div>
                  <div className="bg-white/5 rounded-lg px-3 py-2 text-center">
                    <p className="text-lg font-bold text-emerald-400">{formatCurrency(insightsData.avgCPA)}</p>
                    <p className="text-[10px] text-zinc-500 uppercase">Avg CPA</p>
                  </div>
                  <div className="bg-white/5 rounded-lg px-3 py-2 text-center">
                    <p className="text-lg font-bold text-blue-400">{insightsData.avgCTR?.toFixed(2)}%</p>
                    <p className="text-[10px] text-zinc-500 uppercase">Avg CTR</p>
                  </div>
                  {/* AI Insights Button */}
                  <button
                    onClick={() => fetchAiInsights(timeframe)}
                    disabled={aiInsightsLoading}
                    className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors disabled:opacity-50"
                  >
                    {aiInsightsLoading ? (
                      <>
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4" />
                        AI Analysis
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* ==================== AI POWERED INSIGHTS ==================== */}
            {aiInsights && (
              <div className="bg-[#09090B] rounded-2xl border border-violet-500/30 overflow-hidden">
                <button
                  className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                  onClick={() => toggleCategory('aiInsights')}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                      <Zap className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-sm font-bold text-violet-400">AI-POWERED INSIGHTS</h3>
                      <p className="text-xs text-zinc-500">GPT-4o analysis of your portfolio</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {aiInsights.smart_scores && (
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        aiInsights.smart_scores.overall_health >= 70 ? 'bg-emerald-500/20 text-emerald-400' :
                        aiInsights.smart_scores.overall_health >= 40 ? 'bg-amber-500/20 text-amber-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        Health: {aiInsights.smart_scores.overall_health}/100
                      </span>
                    )}
                    <svg className={`w-5 h-5 text-zinc-400 transition-transform ${expandedCategories.aiInsights ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                {expandedCategories.aiInsights && (
                  <div className="p-4 pt-0 space-y-4">
                    {/* AI Summary */}
                    {aiInsights.summary && (
                      <div className="bg-violet-500/10 rounded-xl p-4 border border-violet-500/20">
                        <h4 className="text-xs font-bold text-violet-400 uppercase mb-2 flex items-center gap-1.5">
                          <svg className="w-4 h-4 text-[#6366f1]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg> Executive Summary
                        </h4>
                        <p className="text-sm text-zinc-300 leading-relaxed">{aiInsights.summary}</p>
                      </div>
                    )}
                    
                    {/* Smart Scores */}
                    {aiInsights.smart_scores && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-white/5 rounded-lg p-3 text-center">
                          <p className={`text-2xl font-bold ${
                            aiInsights.smart_scores.overall_health >= 70 ? 'text-emerald-400' :
                            aiInsights.smart_scores.overall_health >= 40 ? 'text-amber-400' : 'text-red-400'
                          }`}>{aiInsights.smart_scores.overall_health}</p>
                          <p className="text-[10px] text-zinc-500 uppercase">Overall Health</p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-blue-400">{aiInsights.smart_scores.efficiency_score}</p>
                          <p className="text-[10px] text-zinc-500 uppercase">Efficiency</p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-cyan-400">{aiInsights.smart_scores.growth_potential}</p>
                          <p className="text-[10px] text-zinc-500 uppercase">Growth Potential</p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3 text-center">
                          <p className={`text-lg font-bold ${
                            aiInsights.smart_scores.risk_level === 'low' ? 'text-emerald-400' :
                            aiInsights.smart_scores.risk_level === 'medium' ? 'text-amber-400' : 'text-red-400'
                          }`}>{aiInsights.smart_scores.risk_level?.toUpperCase()}</p>
                          <p className="text-[10px] text-zinc-500 uppercase">Risk Level</p>
                        </div>
                      </div>
                    )}

                    {/* AI Insights List */}
                    {aiInsights.ai_insights && aiInsights.ai_insights.length > 0 && (
                      <div className="bg-white/5 rounded-xl p-4">
                        <h4 className="text-xs font-bold text-violet-400 uppercase mb-3 flex items-center gap-1.5">
                          <svg className="w-4 h-4 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg> Key Insights
                        </h4>
                        <div className="space-y-3">
                          {aiInsights.ai_insights.map((insight, i) => (
                            <div key={i} className={`rounded-lg p-3 border ${
                              insight.impact === 'high' ? 'bg-red-500/10 border-red-500/20' :
                              insight.impact === 'medium' ? 'bg-amber-500/10 border-amber-500/20' :
                              'bg-blue-500/10 border-blue-500/20'
                            }`}>
                              <div className="flex items-start justify-between gap-2">
                                <h5 className="text-sm font-bold text-white">{insight.title}</h5>
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                  insight.impact === 'high' ? 'bg-red-500/20 text-red-400' :
                                  insight.impact === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                                  'bg-blue-500/20 text-blue-400'
                                }`}>{insight.impact}</span>
                              </div>
                              <p className="text-xs text-zinc-400 mt-1">{insight.description}</p>
                              {insight.action && (
                                <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1">
                                  <span>→</span> {insight.action}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* AI Alerts */}
                    {aiInsights.ai_alerts && aiInsights.ai_alerts.length > 0 && (
                      <div className="bg-red-500/5 rounded-xl p-4 border border-red-500/20">
                        <h4 className="text-xs font-bold text-red-400 uppercase mb-3 flex items-center gap-1.5">
                          <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg> Critical Alerts
                        </h4>
                        <div className="space-y-2">
                          {aiInsights.ai_alerts.map((alert, i) => (
                            <div key={i} className={`rounded-lg p-3 ${
                              alert.severity === 'critical' ? 'bg-red-500/10' : 'bg-amber-500/10'
                            }`}>
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${
                                  alert.severity === 'critical' ? 'bg-red-400 animate-pulse' : 'bg-amber-400'
                                }`}></span>
                                <h5 className="text-sm font-bold text-white">{alert.title}</h5>
                              </div>
                              <p className="text-xs text-zinc-400 mt-1">{alert.message}</p>
                              {alert.action_required && (
                                <p className="text-xs text-cyan-400 mt-2">Action: {alert.action_required}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Top Recommendations */}
                    {aiInsights.recommendations && aiInsights.recommendations.length > 0 && (
                      <div className="bg-emerald-500/5 rounded-xl p-4 border border-emerald-500/20">
                        <h4 className="text-xs font-bold text-emerald-400 uppercase mb-3 flex items-center gap-1.5">
                          <svg className="w-4 h-4 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> Top Recommendations
                        </h4>
                        <div className="space-y-2">
                          {aiInsights.recommendations.map((rec, i) => (
                            <div key={i} className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
                              <span className="w-6 h-6 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center text-xs font-bold">
                                {rec.priority}
                              </span>
                              <div>
                                <p className="text-sm text-white">{rec.action}</p>
                                <p className="text-xs text-zinc-500 mt-1">Impact: {rec.expected_impact}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* AI Insights Error State */}
            {aiInsightsError && (
              <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/20">
                <p className="text-sm text-red-400">AI Analysis Error: {aiInsightsError}</p>
                <button 
                  onClick={() => fetchAiInsights(timeframe)}
                  className="mt-2 text-xs text-white bg-red-500/20 hover:bg-red-500/30 px-3 py-1.5 rounded"
                >
                  Retry
                </button>
              </div>
            )}

            {/* CATEGORY 1: IMMEDIATE ACTION REQUIRED */}
            <div className="bg-[#09090B] rounded-xl border border-red-500/20 overflow-hidden">
              <button 
                className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                onClick={() => toggleCategory('immediate')}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                  </div>
                  <div className="text-left">
                    <h3 className="text-sm font-bold text-red-400">IMMEDIATE ACTION REQUIRED</h3>
                    <p className="text-xs text-zinc-500">Budget drains, zero conversions, cost spikes</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {insightsData.budgetDrains.length > 0 && (
                    <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs font-bold">
                      {insightsData.budgetDrains.length} drains
                    </span>
                  )}
                  {insightsData.budgetDrains.filter(c => c.conversions === 0).length > 0 && (
                    <span className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded text-xs font-bold">
                      {insightsData.budgetDrains.filter(c => c.conversions === 0).length} zero conv
                    </span>
                  )}
                  {insightsData.costSpikeAlerts.length > 0 && (
                    <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-xs font-bold">
                      {insightsData.costSpikeAlerts.length} cost spikes
                    </span>
                  )}
                  <svg className={`w-5 h-5 text-zinc-400 transition-transform ${expandedCategories.immediate ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              {expandedCategories.immediate && (
                <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Budget Drains */}
                  <div className="bg-red-500/5 rounded-xl p-4 border border-red-500/10">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-bold text-red-400 uppercase flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" /></svg> Budget Drains
                        <span className="px-1.5 py-0.5 bg-red-500/20 rounded text-[10px]">
                          {insightsData.budgetDrains.length}
                        </span>
                      </h4>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                      {insightsData.budgetDrains.length > 0 ? insightsData.budgetDrains.map((camp, i) => (
                        <div key={i} className="bg-white/5 rounded-lg p-2">
                          <p className="text-xs text-white font-medium truncate" title={camp.name}>{camp.name}</p>
                          <p className="text-[10px] text-zinc-500">{camp.clientName}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-red-400 font-mono">{formatCurrency(camp.spend)}</span>
                            <span className="text-[10px] text-zinc-500">{camp.conversions} conv</span>
                          </div>
                        </div>
                      )) : <p className="text-xs text-zinc-600 text-center py-4">No budget drains detected ✓</p>}
                    </div>
                  </div>

                  {/* Zero Conversions */}
                  <div className="bg-orange-500/5 rounded-xl p-4 border border-orange-500/10">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-bold text-orange-400 uppercase flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg> Zero Conversions
                        <span className="px-1.5 py-0.5 bg-orange-500/20 rounded text-[10px]">
                          {insightsData.budgetDrains.filter(c => c.conversions === 0).length}
                        </span>
                      </h4>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                      {insightsData.budgetDrains.filter(c => c.conversions === 0).length > 0 ? 
                        insightsData.budgetDrains.filter(c => c.conversions === 0).map((camp, i) => (
                          <div key={i} className="bg-white/5 rounded-lg p-2">
                            <p className="text-xs text-white font-medium truncate" title={camp.name}>{camp.name}</p>
                            <p className="text-[10px] text-zinc-500">{camp.clientName}</p>
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-xs text-orange-400 font-mono">{formatCurrency(camp.spend)}</span>
                              <span className="text-[10px] text-orange-300">{camp.type}</span>
                            </div>
                          </div>
                        )) : <p className="text-xs text-zinc-600 text-center py-4">All campaigns converting ✓</p>}
                    </div>
                  </div>

                  {/* Cost Spike Alerts */}
                  <div className="bg-amber-500/5 rounded-xl p-4 border border-amber-500/10">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-bold text-amber-400 uppercase flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg> Cost Spikes
                        <span className="px-1.5 py-0.5 bg-amber-500/20 rounded text-[10px]">
                          {insightsData.costSpikeAlerts.length}
                        </span>
                      </h4>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                      {insightsData.costSpikeAlerts.length > 0 ? insightsData.costSpikeAlerts.map((camp, i) => (
                        <div key={i} className="bg-white/5 rounded-lg p-2">
                          <p className="text-xs text-white font-medium truncate" title={camp.name}>{camp.name}</p>
                          <p className="text-[10px] text-zinc-500">{camp.clientName}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-amber-400">CPA: {formatCurrency(camp.cpa)}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${camp.severity === 'critical' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                              +{camp.spikePercent}%
                            </span>
                          </div>
                        </div>
                      )) : <p className="text-xs text-zinc-600 text-center py-4">No cost spikes detected ✓</p>}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* CATEGORY 2: PERFORMANCE TRENDS */}
            <div className="bg-[#09090B] rounded-xl border border-blue-500/20 overflow-hidden">
              <button 
                className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                onClick={() => toggleCategory('trends')}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#6366f1]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                  </div>
                  <div className="text-left">
                    <h3 className="text-sm font-bold text-blue-400">PERFORMANCE TRENDS</h3>
                    <p className="text-xs text-zinc-500">Rising stars, declining campaigns, quality issues</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {insightsData.risingStars.length > 0 && (
                    <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs font-bold">
                      {insightsData.risingStars.length} rising
                    </span>
                  )}
                  {insightsData.decliningPerformers.length > 0 && (
                    <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs font-bold">
                      {insightsData.decliningPerformers.length} declining
                    </span>
                  )}
                  <svg className={`w-5 h-5 text-zinc-400 transition-transform ${expandedCategories.trends ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              {expandedCategories.trends && (
                <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Rising Stars */}
                  <div className="bg-emerald-500/5 rounded-xl p-4 border border-emerald-500/10">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-bold text-emerald-400 uppercase flex items-center gap-1.5">
                        <span>🚀</span> Rising Stars
                        <span className="px-1.5 py-0.5 bg-emerald-500/20 rounded text-[10px]">
                          {insightsData.risingStars.length}
                        </span>
                      </h4>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                      {insightsData.risingStars.length > 0 ? insightsData.risingStars.map((camp, i) => (
                        <div key={i} className="bg-white/5 rounded-lg p-2">
                          <p className="text-xs text-white font-medium truncate" title={camp.name}>{camp.name}</p>
                          <p className="text-[10px] text-zinc-500">{camp.clientName}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-emerald-400">CPA: {formatCurrency(camp.cpa)}</span>
                            <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded">
                              -{camp.efficiency}% vs avg
                            </span>
                          </div>
                        </div>
                      )) : <p className="text-xs text-zinc-600 text-center py-4">No rising stars yet</p>}
                    </div>
                  </div>

                  {/* Declining Performers */}
                  <div className="bg-red-500/5 rounded-xl p-4 border border-red-500/10">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-bold text-red-400 uppercase flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" /></svg> Declining
                        <span className="px-1.5 py-0.5 bg-red-500/20 rounded text-[10px]">
                          {insightsData.decliningPerformers.length}
                        </span>
                      </h4>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                      {insightsData.decliningPerformers.length > 0 ? insightsData.decliningPerformers.map((camp, i) => (
                        <div key={i} className="bg-white/5 rounded-lg p-2">
                          <p className="text-xs text-white font-medium truncate" title={camp.name}>{camp.name}</p>
                          <p className="text-[10px] text-zinc-500">{camp.clientName}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-red-400">CPA: {formatCurrency(camp.cpa)}</span>
                            <span className="text-[10px] px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded">
                              +{camp.cpaIncrease}% vs avg
                            </span>
                          </div>
                        </div>
                      )) : <p className="text-xs text-zinc-600 text-center py-4">No declining campaigns ✓</p>}
                    </div>
                  </div>

                  {/* Quality Score Issues (Low CTR) */}
                  <div className="bg-purple-500/5 rounded-xl p-4 border border-purple-500/10">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-bold text-purple-400 uppercase flex items-center gap-1.5">
                        <span>⭐</span> Quality Issues
                        <span className="px-1.5 py-0.5 bg-purple-500/20 rounded text-[10px]">
                          {insightsData.qualityScoreIssues.length}
                        </span>
                      </h4>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                      {insightsData.qualityScoreIssues.length > 0 ? insightsData.qualityScoreIssues.slice(0, 5).map((camp, i) => (
                        <div key={i} className="bg-white/5 rounded-lg p-2">
                          <p className="text-xs text-white font-medium truncate" title={camp.name}>{camp.name}</p>
                          <p className="text-[10px] text-zinc-500">{camp.clientName}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-purple-400">CTR: {camp.ctr?.toFixed(2)}%</span>
                            <span className="text-[10px] text-zinc-500">{camp.type}</span>
                          </div>
                        </div>
                      )) : <p className="text-xs text-zinc-600 text-center py-4">All quality scores healthy ✓</p>}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* CATEGORY 3: CONVERSION OPTIMIZATION */}
            <div className="bg-[#09090B] rounded-xl border border-cyan-500/20 overflow-hidden">
              <button 
                className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                onClick={() => toggleCategory('conversion')}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  </div>
                  <div className="text-left">
                    <h3 className="text-sm font-bold text-cyan-400">CONVERSION OPTIMIZATION</h3>
                    <p className="text-xs text-zinc-500">Landing page issues, CTR fixes, conversion gaps</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {insightsData.clickToConvGap.length > 0 && (
                    <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded text-xs font-bold">
                      {insightsData.clickToConvGap.length} landing issues
                    </span>
                  )}
                  {insightsData.quickWins.length > 0 && (
                    <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-xs font-bold">
                      {insightsData.quickWins.length} low CTR
                    </span>
                  )}
                  <svg className={`w-5 h-5 text-zinc-400 transition-transform ${expandedCategories.conversion ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              {expandedCategories.conversion && (
                <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Click-to-Conversion Gap */}
                  <div className="bg-cyan-500/5 rounded-xl p-4 border border-cyan-500/10">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-bold text-cyan-400 uppercase flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.828 14.828a4 4 0 015.656 0l4 4a4 4 0 01-5.656 5.656l-1.102-1.101" /></svg> Landing Page Issues
                        <span className="px-1.5 py-0.5 bg-cyan-500/20 rounded text-[10px]">
                          {insightsData.clickToConvGap.length}
                        </span>
                      </h4>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                      {insightsData.clickToConvGap.length > 0 ? insightsData.clickToConvGap.map((camp, i) => (
                        <div key={i} className="bg-white/5 rounded-lg p-2">
                          <p className="text-xs text-white font-medium truncate" title={camp.name}>{camp.name}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-cyan-400">{formatNumber(camp.clicks)} clicks</span>
                            <span className="text-[10px] text-zinc-500">Conv: {camp.convRate?.toFixed(2)}%</span>
                          </div>
                          <p className="text-[10px] text-cyan-300 mt-1">Potential: +{camp.potentialConv} conv</p>
                        </div>
                      )) : <p className="text-xs text-zinc-600 text-center py-4">Landing pages converting well ✓</p>}
                    </div>
                  </div>

                  {/* Impression Share Opportunity */}
                  <div className="bg-indigo-500/5 rounded-xl p-4 border border-indigo-500/10">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-bold text-indigo-400 uppercase flex items-center gap-1.5">
                        <span>👁️</span> Impression Share Opp
                        <span className="px-1.5 py-0.5 bg-indigo-500/20 rounded text-[10px]">
                          {insightsData.impressionShareOpp.length}
                        </span>
                      </h4>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                      {insightsData.impressionShareOpp.length > 0 ? insightsData.impressionShareOpp.map((camp, i) => (
                        <div key={i} className="bg-white/5 rounded-lg p-2">
                          <p className="text-xs text-white font-medium truncate" title={camp.name}>{camp.name}</p>
                          <span className="text-[10px] text-indigo-400">CTR: {camp.ctr?.toFixed(2)}% (avg: {insightsData.avgCTR?.toFixed(2)}%)</span>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[10px] text-zinc-500">{formatNumber(camp.impressions)} impr</span>
                            <span className="text-[10px] text-indigo-300">+{camp.potentialClicks} clicks potential</span>
                          </div>
                        </div>
                      )) : <p className="text-xs text-zinc-600 text-center py-4">CTRs look healthy ✓</p>}
                    </div>
                  </div>

                  {/* Quick Wins (Low CTR Search) */}
                  <div className="bg-amber-500/5 rounded-xl p-4 border border-amber-500/10">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-bold text-amber-400 uppercase flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg> Ad Copy Refresh
                        <span className="px-1.5 py-0.5 bg-amber-500/20 rounded text-[10px]">
                          {insightsData.quickWins.length}
                        </span>
                      </h4>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                      {insightsData.quickWins.length > 0 ? insightsData.quickWins.map((camp, i) => (
                        <div key={i} className="bg-white/5 rounded-lg p-2">
                          <p className="text-xs text-white font-medium truncate" title={camp.name}>{camp.name}</p>
                          <p className="text-[10px] text-zinc-500">{camp.clientName}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-amber-400">CTR: {camp.ctr?.toFixed(2)}%</span>
                            <span className="text-[10px] text-zinc-500">{formatCurrency(camp.spend)} spend</span>
                          </div>
                        </div>
                      )) : <p className="text-xs text-zinc-600 text-center py-4">Search ads performing well ✓</p>}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* CATEGORY 4: BUDGET OPTIMIZATION */}
            <div className="bg-[#09090B] rounded-xl border border-emerald-500/20 overflow-hidden">
              <button 
                className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                onClick={() => toggleCategory('budget')}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-lg">💰</span>
                  </div>
                  <div className="text-left">
                    <h3 className="text-sm font-bold text-emerald-400">BUDGET OPTIMIZATION</h3>
                    <p className="text-xs text-zinc-500">Scale opportunities, hidden gems, misallocated budgets</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {insightsData.scaleOpportunities.length > 0 && (
                    <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs font-bold">
                      {insightsData.scaleOpportunities.length} scale ready
                    </span>
                  )}
                  {insightsData.hiddenGems.length > 0 && (
                    <span className="px-2 py-1 bg-teal-500/20 text-teal-400 rounded text-xs font-bold">
                      {insightsData.hiddenGems.length} hidden gems
                    </span>
                  )}
                  {insightsData.budgetMisallocated.length > 0 && (
                    <span className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded text-xs font-bold">
                      {insightsData.budgetMisallocated.length} misallocated
                    </span>
                  )}
                  <svg className={`w-5 h-5 text-zinc-400 transition-transform ${expandedCategories.budget ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              {expandedCategories.budget && (
                <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Scale Opportunities */}
                  <div className="bg-emerald-500/5 rounded-xl p-4 border border-emerald-500/10">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-bold text-emerald-400 uppercase flex items-center gap-1.5">
                        <span>🚀</span> Scale Ready
                        <span className="px-1.5 py-0.5 bg-emerald-500/20 rounded text-[10px]">
                          {insightsData.scaleOpportunities.length}
                        </span>
                      </h4>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                      {insightsData.scaleOpportunities.length > 0 ? insightsData.scaleOpportunities.map((camp, i) => (
                        <div key={i} className="bg-white/5 rounded-lg p-2">
                          <p className="text-xs text-white font-medium truncate" title={camp.name}>{camp.name}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-emerald-400">CPA: {formatCurrency(camp.cpa)}</span>
                            <span className="text-[10px] text-zinc-500">{formatCurrency(camp.spend)}</span>
                          </div>
                        </div>
                      )) : <p className="text-xs text-zinc-600 text-center py-4">No scale opportunities yet</p>}
                    </div>
                  </div>

                  {/* Hidden Gems */}
                  <div className="bg-teal-500/5 rounded-xl p-4 border border-teal-500/10">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-bold text-teal-400 uppercase flex items-center gap-1.5">
                        <span>💎</span> Hidden Gems
                        <span className="px-1.5 py-0.5 bg-teal-500/20 rounded text-[10px]">
                          {insightsData.hiddenGems.length}
                        </span>
                      </h4>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                      {insightsData.hiddenGems.length > 0 ? insightsData.hiddenGems.map((camp, i) => (
                        <div key={i} className="bg-white/5 rounded-lg p-2">
                          <p className="text-xs text-white font-medium truncate" title={camp.name}>{camp.name}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-teal-400">CPA: {formatCurrency(camp.cpa)}</span>
                            <span className="text-[10px] px-1.5 py-0.5 bg-teal-500/20 text-teal-400 rounded">
                              {camp.efficiencyRating}x efficient
                            </span>
                          </div>
                          <p className="text-[10px] text-zinc-500 mt-1">Scale: {camp.scalePotential}</p>
                        </div>
                      )) : <p className="text-xs text-zinc-600 text-center py-4">No hidden gems found</p>}
                    </div>
                  </div>

                  {/* Budget Misallocated */}
                  <div className="bg-orange-500/5 rounded-xl p-4 border border-orange-500/10">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-bold text-orange-400 uppercase flex items-center gap-1.5">
                        <span>🔄</span> Misallocated
                        <span className="px-1.5 py-0.5 bg-orange-500/20 rounded text-[10px]">
                          {insightsData.budgetMisallocated.length}
                        </span>
                      </h4>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                      {insightsData.budgetMisallocated.length > 0 ? insightsData.budgetMisallocated.map((camp, i) => (
                        <div key={i} className="bg-white/5 rounded-lg p-2">
                          <p className="text-xs text-white font-medium truncate" title={camp.name}>{camp.name}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-orange-400">Wasted: {formatCurrency(camp.wastedAmount)}</span>
                          </div>
                          <p className="text-[10px] text-zinc-500 mt-1 truncate">Better: {camp.betterAlternative}</p>
                        </div>
                      )) : <p className="text-xs text-zinc-600 text-center py-4">Budgets well allocated ✓</p>}
                    </div>
                  </div>

                  {/* Spend by Client Mini Chart */}
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <h4 className="text-xs font-bold text-zinc-400 uppercase mb-3 flex items-center gap-1.5">
                      <svg className="w-4 h-4 text-[#6366f1]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg> Spend by Client
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                      {insightsData.spendByClient.slice(0, 5).map((client, i) => {
                        const maxSpend = insightsData.spendByClient[0]?.spend || 1;
                        const width = (client.spend / maxSpend) * 100;
                        return (
                          <div key={i} className="space-y-1">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-zinc-400 truncate max-w-[100px]" title={client.fullName}>{client.name}</span>
                              <span className="text-white font-mono">{formatCurrency(client.spend)}</span>
                            </div>
                            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-[#4285F4] to-[#34A853] rounded-full transition-colors"
                                style={{ width: `${width}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* CATEGORY 5: EFFICIENCY METRICS */}
            <div className="bg-[#09090B] rounded-xl border border-purple-500/20 overflow-hidden">
              <button 
                className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                onClick={() => toggleCategory('efficiency')}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                  </div>
                  <div className="text-left">
                    <h3 className="text-sm font-bold text-purple-400">EFFICIENCY METRICS</h3>
                    <p className="text-xs text-zinc-500">Top performers, PMax insights, CPC/CPM analysis</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {insightsData.topEfficient.length > 0 && (
                    <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs font-bold">
                      {insightsData.topEfficient.length} top performers
                    </span>
                  )}
                  {insightsData.pmaxOpportunities.length > 0 && (
                    <span className="px-2 py-1 bg-teal-500/20 text-teal-400 rounded text-xs font-bold">
                      {insightsData.pmaxOpportunities.length} PMax
                    </span>
                  )}
                  {insightsData.cpcOutliers.length > 0 && (
                    <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs font-bold">
                      {insightsData.cpcOutliers.length} CPC outliers
                    </span>
                  )}
                  <svg className={`w-5 h-5 text-zinc-400 transition-transform ${expandedCategories.efficiency ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              {expandedCategories.efficiency && (
                <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Top Performers */}
                  <div className="bg-emerald-500/5 rounded-xl p-4 border border-emerald-500/10">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-bold text-emerald-400 uppercase flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg> Top Performers
                        <span className="px-1.5 py-0.5 bg-emerald-500/20 rounded text-[10px]">
                          {insightsData.topEfficient.length}
                        </span>
                      </h4>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                      {insightsData.topEfficient.slice(0, 5).map((camp, i) => (
                        <div key={i} className="bg-white/5 rounded-lg p-2">
                          <p className="text-xs text-white font-medium truncate" title={camp.name}>{camp.name}</p>
                          <p className="text-[10px] text-zinc-500">{camp.clientName}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-emerald-400">CPA: {formatCurrency(camp.cpa)}</span>
                            <span className="text-[10px] text-zinc-500">{camp.conversions} conv</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* PMax Opportunities */}
                  <div className="bg-teal-500/5 rounded-xl p-4 border border-teal-500/10">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-bold text-teal-400 uppercase flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg> Performance Max
                        <span className="px-1.5 py-0.5 bg-teal-500/20 rounded text-[10px]">
                          {insightsData.pmaxOpportunities.length}
                        </span>
                      </h4>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                      {insightsData.pmaxOpportunities.length > 0 ? insightsData.pmaxOpportunities.map((camp, i) => (
                        <div key={i} className="bg-white/5 rounded-lg p-2">
                          <p className="text-xs text-white font-medium truncate" title={camp.name}>{camp.name}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-teal-400">CPA: {formatCurrency(camp.cpa)}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                              camp.performance === 'Above Avg' ? 'bg-emerald-500/20 text-emerald-400' :
                              camp.performance === 'Average' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-red-500/20 text-red-400'
                            }`}>
                              {camp.performance}
                            </span>
                          </div>
                        </div>
                      )) : <p className="text-xs text-zinc-600 text-center py-4">No PMax campaigns</p>}
                    </div>
                  </div>

                  {/* CPM Watch */}
                  <div className="bg-amber-500/5 rounded-xl p-4 border border-amber-500/10">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-bold text-amber-400 uppercase flex items-center gap-1.5">
                        <span>📺</span> High CPM
                        <span className="px-1.5 py-0.5 bg-amber-500/20 rounded text-[10px]">
                          {insightsData.cpmWatch.length}
                        </span>
                      </h4>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                      {insightsData.cpmWatch.length > 0 ? insightsData.cpmWatch.map((camp, i) => (
                        <div key={i} className="bg-white/5 rounded-lg p-2">
                          <p className="text-xs text-white font-medium truncate" title={camp.name}>{camp.name}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-amber-400">CPM: {formatCurrency(camp.cpm)}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${camp.severity === 'critical' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                              +{camp.cpmIncrease}%
                            </span>
                          </div>
                        </div>
                      )) : <p className="text-xs text-zinc-600 text-center py-4">CPMs look healthy ✓</p>}
                    </div>
                  </div>

                  {/* CPC Outliers */}
                  <div className="bg-red-500/5 rounded-xl p-4 border border-red-500/10">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-bold text-red-400 uppercase flex items-center gap-1.5">
                        <span>🖱️</span> CPC Outliers
                        <span className="px-1.5 py-0.5 bg-red-500/20 rounded text-[10px]">
                          {insightsData.cpcOutliers.length}
                        </span>
                      </h4>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                      {insightsData.cpcOutliers.length > 0 ? insightsData.cpcOutliers.map((camp, i) => (
                        <div key={i} className="bg-white/5 rounded-lg p-2">
                          <p className="text-xs text-white font-medium truncate" title={camp.name}>{camp.name}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-red-400">CPC: {formatCurrency(camp.cpc)}</span>
                            <span className="text-[10px] text-zinc-500">Save: {formatCurrency(camp.potentialSavings)}</span>
                          </div>
                        </div>
                      )) : <p className="text-xs text-zinc-600 text-center py-4">CPCs look healthy ✓</p>}
                    </div>
                  </div>

                  {/* Campaign Type Performance - Full Width */}
                  {insightsData.campaignTypeData.length > 0 && (
                    <div className="md:col-span-2 lg:col-span-4 bg-white/5 rounded-xl p-4 border border-white/10">
                      <h4 className="text-xs font-bold text-zinc-400 uppercase mb-3 flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg> Performance by Campaign Type
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        {insightsData.campaignTypeData.map((data, i) => (
                          <div key={i} className="bg-white/5 rounded-lg p-3 text-center">
                            <p className="text-lg font-bold text-white">{data.type}</p>
                            <p className="text-xs text-zinc-500 mt-1">{data.campaigns} campaigns</p>
                            <div className="mt-2 pt-2 border-t border-white/5">
                              <p className="text-xs text-emerald-400">{formatCurrency(data.spend)}</p>
                              <p className="text-[10px] text-zinc-500">{data.conversions} conv</p>
                              {data.cpa > 0 && (
                                <p className="text-[10px] text-blue-400 mt-1">CPA: {formatCurrency(data.cpa)}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Search Leaders */}
                  {insightsData.searchLeaders.length > 0 && (
                    <div className="md:col-span-2 bg-blue-500/5 rounded-xl p-4 border border-blue-500/10">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-xs font-bold text-blue-400 uppercase flex items-center gap-1.5">
                          <span>🔍</span> Search Campaign Leaders
                          <span className="px-1.5 py-0.5 bg-blue-500/20 rounded text-[10px]">
                            {insightsData.searchLeaders.length}
                          </span>
                        </h4>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {insightsData.searchLeaders.map((camp, i) => (
                          <div key={i} className="bg-white/5 rounded-lg p-2">
                            <p className="text-xs text-white font-medium truncate" title={camp.name}>{camp.name}</p>
                            <p className="text-[10px] text-zinc-500">{camp.clientName}</p>
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-xs text-blue-400">CPA: {formatCurrency(camp.cpa)}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                camp.qualityIndicator === 'Excellent' ? 'bg-emerald-500/20 text-emerald-400' :
                                camp.qualityIndicator === 'Good' ? 'bg-blue-500/20 text-blue-400' :
                                'bg-zinc-500/20 text-zinc-400'
                              }`}>
                                {camp.qualityIndicator} CTR
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Performance Charts Section */}
          <PerformanceCharts 
            clients={platformClients} 
            platform="GOOGLE"
          />

          {/* Account Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              value={accountSearch}
              onChange={e => setAccountSearch(e.target.value)}
              placeholder="Search accounts..."
              className="bg-[#09090B] border border-white/10 rounded-lg px-3 py-2 pl-9 text-sm text-white placeholder-white/40 w-full focus:outline-none focus:border-white/20"
            />
            {accountSearch && (
              <button onClick={() => setAccountSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2">
            {[
              { id: 'all', label: 'All Clients', count: platformClients.length },
              { id: 'good', label: 'Good', count: summaryStats.goodClients, color: 'text-emerald-400' },
              { id: 'warning', label: 'Warning', count: summaryStats.warningClients, color: 'text-amber-400' },
              { id: 'critical', label: 'Critical', count: summaryStats.criticalClients, color: 'text-red-400' }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilterStatus(f.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 ${
                  filterStatus === f.id
                    ? 'bg-[#4285F4]/20 text-[#4285F4] border border-[#4285F4]/30'
                    : 'bg-white/5 text-zinc-400 hover:text-white border border-transparent'
                }`}
              >
                <span className={f.color}>{f.label}</span>
                <span className="px-1.5 py-0.5 rounded bg-white/10 text-[10px]">{f.count}</span>
              </button>
            ))}
          </div>

          {/* Client Cards - Already sorted by spend descending */}
          <div className="space-y-4">
            {filteredClients.map(client => (
              <ClientCard
                key={client.id}
                client={client}
                campaigns={client.campaigns || []}
                isExpanded={expandedClient === client.id}
                onToggle={() => setExpandedClient(expandedClient === client.id ? null : client.id)}
                onAnalyze={() => setAiAnalysisClient({ id: client.id, name: client.name })}
                addToast={addToast}
              />
            ))}

            {!loading && platformClients.length === 0 && (
              <div className="text-center py-12 text-zinc-400">No Google accounts found</div>
            )}

            {filteredClients.length === 0 && platformClients.length > 0 && (
              <div className="text-center py-16 bg-[#09090B] rounded-2xl border border-white/10">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/5 flex items-center justify-center">
                  <svg className="w-8 h-8 text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">
                  {filterStatus !== 'all' ? 'No Clients Match Filter' : 'No Google Data for This Period'}
                </h3>
                <p className="text-zinc-500 mb-4">
                  {filterStatus !== 'all'
                    ? 'Try changing the filter above.'
                    : 'Google Ads data may still be syncing. Try selecting "Last 30 Days" or click Refresh.'}
                </p>
                {filterStatus === 'all' && (
                  <div className="flex items-center justify-center gap-3">
                    <button
                      onClick={() => handleTimeframeChange('last_30_days')}
                      className="px-4 py-2 bg-[#4285F4]/20 text-[#4285F4] border border-[#4285F4]/30 rounded-xl text-sm font-medium hover:bg-[#4285F4]/30 transition-colors"
                    >
                      Show Last 30 Days
                    </button>
                    <button
                      onClick={() => fetchAllData()}
                      className="px-4 py-2 bg-white/5 text-zinc-400 border border-white/10 rounded-xl text-sm font-medium hover:bg-white/10 transition-colors"
                    >
                      Refresh
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Health Tab */}
      {activeTab === 'health' && (
        <div className="space-y-6">
          {/* Health Summary Banner */}
          <div className="bg-gradient-to-r from-emerald-500/10 via-[#0D0D0F] to-transparent rounded-2xl border border-emerald-500/20 p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <svg className="w-7 h-7 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Google Accounts Health</h2>
                  <p className="text-zinc-400 text-sm">AI-powered analysis of your ad accounts</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="bg-emerald-500/10 rounded-xl px-4 py-2 text-center">
                  <p className="text-2xl font-bold text-emerald-400">{healthData?.healthy?.length || 0}</p>
                  <p className="text-[10px] text-zinc-500 uppercase">Healthy</p>
                </div>
                <div className="bg-amber-500/10 rounded-xl px-4 py-2 text-center">
                  <p className="text-2xl font-bold text-amber-400">{healthData?.warning?.length || 0}</p>
                  <p className="text-[10px] text-zinc-500 uppercase">Warning</p>
                </div>
                <div className="bg-red-500/10 rounded-xl px-4 py-2 text-center">
                  <p className="text-2xl font-bold text-red-400">{healthData?.critical?.length || 0}</p>
                  <p className="text-[10px] text-zinc-500 uppercase">Critical</p>
                </div>
              </div>
            </div>
          </div>

          {/* Critical Accounts */}
          {healthData?.critical?.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-red-400 flex items-center gap-2">
                <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg> Critical - Needs Immediate Attention ({healthData.critical.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {healthData.critical.map(account => (
                  <div key={account.id} className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 hover:border-red-500/40 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-white">{account.name}</h4>
                        <p className="text-xs text-zinc-500">Health Score: {account.health_score}%</p>
                      </div>
                      <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-[10px] font-bold">CRITICAL</span>
                    </div>
                    {account.issues?.length > 0 && (
                      <div className="space-y-1">
                        {account.issues.slice(0, 3).map((issue, i) => (
                          <p key={i} className="text-xs text-red-300">• {issue}</p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Warning Accounts */}
          {healthData?.warning?.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-amber-400 flex items-center gap-2">
                <svg className="w-4 h-4 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg> Warning - Requires Review ({healthData.warning.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {healthData.warning.map(account => (
                  <div key={account.id} className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 hover:border-amber-500/40 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-white">{account.name}</h4>
                        <p className="text-xs text-zinc-500">Health Score: {account.health_score}%</p>
                      </div>
                      <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-[10px] font-bold">WARNING</span>
                    </div>
                    {account.issues?.length > 0 && (
                      <div className="space-y-1">
                        {account.issues.slice(0, 3).map((issue, i) => (
                          <p key={i} className="text-xs text-amber-300">• {issue}</p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Healthy Accounts */}
          {healthData?.healthy?.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-emerald-400 flex items-center gap-2">
                <svg className="w-4 h-4 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> Healthy ({healthData.healthy.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {healthData.healthy.slice(0, 6).map(account => (
                  <div key={account.id} className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-white truncate">{account.name}</h4>
                      <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-[10px] font-bold">{account.health_score}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {(!healthData || healthData.total === 0) && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-8 text-center">
              <p className="text-zinc-400">No health data available yet. Run sync to get health analysis.</p>
            </div>
          )}
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="space-y-6">
          {/* Alerts Header */}
          <div className="bg-gradient-to-r from-amber-500/10 via-[#0D0D0F] to-transparent rounded-2xl border border-amber-500/20 p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-amber-500/20 rounded-xl flex items-center justify-center">
                  <svg className="w-7 h-7 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Google Alerts</h2>
                  <p className="text-zinc-400 text-sm">
                    {alerts.length} alerts • {alertsUnreadCount} unread
                  </p>
                </div>
              </div>
              <button
                onClick={fetchAlerts}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm text-white transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Alerts List */}
          {alerts.length > 0 ? (
            <div className="space-y-3">
              {alerts.map(alert => {
                const severityConfig = {
                  critical: { bg: 'bg-red-500/5', border: 'border-red-500/20', badge: 'bg-red-500/20 text-red-400' },
                  high: { bg: 'bg-orange-500/5', border: 'border-orange-500/20', badge: 'bg-orange-500/20 text-orange-400' },
                  medium: { bg: 'bg-amber-500/5', border: 'border-amber-500/20', badge: 'bg-amber-500/20 text-amber-400' },
                  low: { bg: 'bg-blue-500/5', border: 'border-blue-500/20', badge: 'bg-blue-500/20 text-blue-400' }
                }[alert.severity] || { bg: 'bg-white/5', border: 'border-white/10', badge: 'bg-white/10 text-white' };

                return (
                  <div 
                    key={alert.id} 
                    className={`${severityConfig.bg} border ${severityConfig.border} rounded-xl p-4 ${!alert.is_read ? 'ring-1 ring-amber-500/30' : ''}`}
                    onClick={() => !alert.is_read && markAlertAsRead(alert.id)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${severityConfig.badge}`}>
                            {alert.severity}
                          </span>
                          <span className="px-2 py-0.5 bg-white/10 text-zinc-400 rounded text-[10px]">
                            {alert.alert_type?.replace(/_/g, ' ')}
                          </span>
                          {!alert.is_read && (
                            <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
                          )}
                        </div>
                        <h4 className="text-sm font-semibold text-white mb-1">{alert.title}</h4>
                        <p className="text-xs text-zinc-400">{alert.message}</p>
                        <p className="text-[10px] text-zinc-600 mt-2">
                          {alert.account_name} • {new Date(alert.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      {!alert.is_resolved && (
                        <button
                          onClick={(e) => { e.stopPropagation(); resolveAlert(alert.id); }}
                          className="px-3 py-1.5 bg-emerald-500/20 text-emerald-400 rounded-lg text-xs font-semibold hover:bg-emerald-500/30 transition-colors"
                        >
                          Resolve
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-8 text-center">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-emerald-400 mb-2">All Clear!</h3>
              <p className="text-zinc-400">No alerts for Google accounts.</p>
            </div>
          )}
        </div>
      )}

      {/* Inactive Tab */}
      {activeTab === 'inactive' && (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-red-500/10 to-transparent rounded-2xl border border-red-500/20 p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-red-500/20 rounded-xl flex items-center justify-center">
                <svg className="w-7 h-7 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Inactive Accounts</h2>
                <p className="text-zinc-400 text-sm">
                  {inactiveAccounts.length} accounts stopped spending and may need recharge
                </p>
              </div>
            </div>
          </div>

          {inactiveAccounts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {inactiveAccounts.map(account => (
                <InactiveAccountCard key={account.account_id || account.id} account={account} />
              ))}
            </div>
          ) : (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-8 text-center">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-emerald-400 mb-2">All Accounts Active!</h3>
              <p className="text-zinc-400">No recently inactive accounts found.</p>
            </div>
          )}
        </div>
      )}

      {/* AI Analysis Modal */}
      <AIAnalysisPanel
        accent="#4285F4"
        clientId={aiAnalysisClient?.id}
        clientName={aiAnalysisClient?.name}
        isOpen={!!aiAnalysisClient}
        onClose={() => setAiAnalysisClient(null)}
      />
    </div>
  );
};

export default GooglePerformance;
